/* ============================================================================
 *  ZENDA — textos da interface, pt-BR
 * ----------------------------------------------------------------------------
 *  Este arquivo é a FONTE DA VERDADE dos tipos: `Dictionary` é derivado dele, e
 *  `content/en.ts` é declarado com esse tipo. Consequência prática — o inglês
 *  **não compila** se faltar uma chave. É a única garantia que importa numa
 *  tradução: a de que nada ficou para trás.
 *
 *  Strings com parte variável são FUNÇÕES, não templates com `{placeholder}`.
 *  Assim o parâmetro é tipado, a ordem das palavras muda livremente entre as
 *  línguas, e não existe interpolação silenciosa que erra em runtime.
 * ========================================================================= */

type Vars = Readonly<Record<string, string | number>>;

export const ptBR = {
  /* ── Chrome do aplicativo ─────────────────────────────────────────────── */
  nav: {
    team: 'Equipe',
    dashboard: 'Painel',
    inbox: 'Conversas',
    clients: 'Clientes',
    agenda: 'Agenda',
    verification: 'Verificação',
    settings: 'Configurações',
    sections: 'Seções',
    signOut: 'Sair',
    language: 'Idioma',
    collapseMenu: 'Recolher o menu',
    expandMenu: 'Expandir o menu',
  },

  custo: {
    doMes: (mes: string) => `Mensagens em ${mes}`,
    quantas: (n: number) => (n === 1 ? '1 mensagem' : `${n.toLocaleString('pt-BR')} mensagens`),
    estimativa: 'estimativa',
    esconder: 'Esconder os custos',
    debitado: (valor: string, dia: string) => `${valor} debitado pela Gupshup até ${dia}`,
    semExtrato: 'A Gupshup ainda não fechou nenhum dia do mês',
    mostrar: 'Mostrar os custos',
    explicacao:
      'Estimativa do que a Meta e a Gupshup cobram pelas mensagens da sua operação neste mês. É só informativo: nada disso é cobrado de você.',
    partes: (meta: string, parceiro: string) => `Meta ${meta} + Gupshup ${parceiro}`,
    daMensagem: (partes: string) => `Custo estimado: ${partes}`,
    gratisNaMeta: (parceiro: string) => `Grátis na Meta, Gupshup ${parceiro}`,
    categoria: {
      service: 'resposta',
      utility: 'utilidade',
      marketing: 'marketing',
      authentication: 'autenticação',
    } as Readonly<Record<string, string>>,
  },

  pages: {
    team: {
      title: 'Equipe',
      subtitle: 'Quem atende com você, e quais médicos cada um compartilha',
    },
    dashboard: { title: 'Painel', subtitle: 'O que precisa da sua atenção agora' },
    inbox: {
      title: 'Conversas',
      subtitle: 'Todos os pacientes, de todos os clientes, numa lista só',
    },
    clients: {
      title: 'Clientes',
      subtitle: 'Os médicos que você atende: cobrança, conexão e volume',
    },
    agenda: { title: 'Agenda', subtitle: 'Consultas por médico' },
    settings: { title: 'Configurações', subtitle: 'Idioma, etiquetas e templates de mensagem' },
    verification: {
      title: 'Verificação da integração',
      subtitle: 'As chamadas que a Meta exige para aprovar o app',
    },
  },

  common: {
    cancel: 'Cancelar',
    save: 'Salvar',
    saving: 'Salvando…',
    add: 'Adicionar',
    remove: 'Remover',
    close: 'Fechar',
    edit: 'Editar',
    open: 'Abrir',
    search: 'Buscar',
    none: '—',
    loading: 'Carregando…',
    understood: 'Entendi',
    copy: 'Copiar',
    copied: 'Copiado',
    day: 'dia',
    days: 'dias',
    month: 'mês',
    months: 'meses',
    minute: 'min',
    hour: 'h',
    perMonth: 'por mês',
    dueDate: 'vencimento',
    now: 'agora',
    networkError: 'Não foi possível falar com o servidor. Confira a internet e tente de novo.',
  },

  player: {
    tocar: 'Tocar',
    pausar: 'Pausar',
    fechar: 'Fechar o player',
    posicao: 'Posição do áudio',
    audio: 'Áudio',
    ouvir: 'Ouvir',
    irParaConversa: 'Ir para a conversa',
  },

  /* ── Configurações ────────────────────────────────────────────────────── */
  config: {
    nome: 'Seu nome na conversa',
    nomeSub:
      'É como a equipe vê você nas mensagens e o que entra no lugar de {atendente}. Ex.: "Ana" em vez de "Ana Paula Souza". Vazio usa o nome completo.',
    nomeSalvo: 'Nome salvo.',
    language: 'Idioma',
    languageSub: 'Vale só para você. Não muda o idioma das mensagens enviadas aos pacientes.',
    tags: 'Etiquetas',
    tagsSub:
      'Marcadores para achar conversas depois. Ex.: "aguardando exame". Valem para todos os clientes.',
    tagColor: 'Cor da etiqueta',
    tagName: 'Nome da etiqueta',
    tagPlaceholder: 'aguardando exame, convênio, remarcar…',
    create: 'Criar',
    unused: 'sem uso',
    usedIn: (n: number) => (n === 1 ? '1 conversa' : `${n} conversas`),
    confirmDelete: (n: number) => (n === 1 ? 'Apagar de 1 conversa?' : `Apagar de ${n} conversas?`),
    no: 'Não',
    delete: 'Apagar',
    deleteTag: 'Apagar esta etiqueta',
    tagsEmpty: 'Nenhuma etiqueta ainda',
    tagsEmptyText: 'Etiquetas ajudam a achar depois: “aguardando exame”, “convênio”, “remarcar”.',

    /* Mensagens prontas */
    quick: 'Mensagens prontas',
    quickSub:
      'Respostas que você manda sempre, para não redigitar. Ex.: confirmação de horário. Só saem com a janela de 24 h aberta, porque não passam pela aprovação da Meta.',
    title: 'Título',
    titlePlaceholder: 'Confirmação de horário',
    appearsIn: 'Aparece em',
    allDoctors: 'Todos os médicos',
    onlyClient: (nome: string) => `Só ${nome}`,
    message: 'Mensagem',
    quickPlaceholder: 'Bom dia {paciente}! Aqui é {atendente}, secretária de {medico}.',
    varsTitle: 'Clique para inserir no texto:',
    varAlways: (rotulo: string) => `${rotulo}: sempre preenchido`,
    varMaybe: (rotulo: string) => `${rotulo}: só se estiver na ficha do paciente`,
    varsNote:
      'As riscadas dependem da ficha do paciente, como a data de nascimento. Se a ficha não tiver, o envio fica parado até você completar à mão.',
    varsUnknown: (lista: string) => `${lista} não existe. Use os botões acima.`,
    quickCreate: 'Criar mensagem',
    quickSave: 'Salvar alterações',
    quickEmpty: 'Nenhuma mensagem pronta ainda',
    quickEmptyText: 'Crie a primeira acima. Ela aparece na conversa, a um clique.',
    scopeAll: 'Todos',
    confirm: 'Confirmar',

    /* Modelos de template */
    models: 'Modelos de template',
    modelsSub:
      'Um texto base para reaproveitar em vários médicos, como o lembrete de véspera. Aqui ele só fica salvo: a aprovação da Meta é pedida na aba Templates de cada médico, porque cada um tem a própria conta.',
    name: 'Nome',
    namePlaceholder: 'lembrete_vespera',
    category: 'Categoria',
    categoryUtility: 'Utilidade: lembrete, confirmação, aviso',
    categoryMarketing: 'Marketing: promoção, novidade',
    modelPlaceholder: 'Olá {{1}}, sua consulta com {{2}} é amanhã às {{3}}. Confirma?',
    modelHint:
      'Regras da Meta: não começar nem terminar com variável. Regra do Zenda: nada de especialidade, procedimento ou diagnóstico, porque o texto aparece na tela de bloqueio do paciente.',
    modelVars: 'A Meta usa variáveis numeradas:',
    examples: (feitos: number, total: number) => `Exemplo de cada variável (${feitos} de ${total})`,
    examplesPlaceholder: 'Marcos\nDra. Helena\n9h',
    examplesHint:
      'Um por linha, na ordem das variáveis. A Meta lê os exemplos para aprovar. Ex.: Marcos, Dra. Helena, 9h.',
    modelSave: 'Salvar modelo',
    modelsEmpty: 'Nenhum modelo ainda',
    modelsEmptyText: 'Crie os que você repete em todo médico novo, como lembrete de véspera.',
    deleteModel: 'Apagar modelo',
    utility: 'Utilidade',
    marketing: 'Marketing',
  },

  aviso: {
    soCasaForaDoTurno: 'Fora do meu turno, só avisar dos médicos da minha operação',
    titulo: 'Nova mensagem no Zenda',
    corpo: 'Um paciente respondeu. Abra para ver.',
    secao: 'Avisos de mensagem nova',
    secaoSub:
      'Valem só neste navegador. A notificação aparece quando a aba está em segundo plano. Com a tela à vista, a própria lista já avisa.',
    som: 'Tocar um som',
    notificacao: 'Mostrar notificação do sistema',
    bloqueada:
      'O navegador bloqueou as notificações deste site. Libere nas permissões do site para ligar.',
    semSuporte: 'Este navegador não suporta notificações.',
    testar: 'Testar',
  },

  /* ── Login ────────────────────────────────────────────────────────────── */
  login: {
    title: 'Entrar',
    greeting: 'Que bom te ver de novo',
    lede: 'As agendas e os WhatsApps dos médicos que você atende, em uma tela só.',
    email: 'E-mail',
    password: 'Senha',
    showPassword: 'Mostrar a senha',
    hidePassword: 'Esconder a senha',
    /* Não há recuperação por e-mail, e um link "esqueci a senha" que não leva a
       lugar nenhum é pior que a ausência dele. Quem administra a operação repõe. */
    lostAccess: 'Perdeu o acesso? Quem administra a operação repõe a sua senha.',
    submit: 'Entrar',
    submitting: 'Entrando…',
    failed: 'Não foi possível entrar.',
    /* A frase sobre a arte é convite, não manual: quem chega nesta tela é a atendente
       começando o turno. Regra de negócio (janela de 24 h, template, Meta) fica dentro
       do produto, onde ela muda o que a pessoa faz.
       Ela também não pode repetir os balões desenhados na arte ("comunicação
       centralizada", "agendas em um só lugar", "mais tempo para o que importa"): a
       mesma frase duas vezes na mesma metade da tela parece defeito. */
    panelTitle: 'Cada médico atendido como se fosse o único',
    panelSub: 'Você abre uma tela, e o dia já está organizado.',
  },

  trocarSenha: {
    title: 'Crie a sua senha',
    lede: 'Você entrou com uma senha temporária. Escolha uma senha sua para continuar.',
    atual: 'Senha temporária',
    nova: 'Nova senha',
    confirmar: 'Repita a nova senha',
    regra: (n: number) => `Pelo menos ${n} caracteres.`,
    naoConfere: 'As duas senhas novas não são iguais.',
    salvar: 'Salvar e continuar',
    salvando: 'Salvando…',
    sair: 'Sair',
  },

  erroDaTela: {
    title: 'Esta tela quebrou',
    lede: 'O erro já foi registrado. Recarregue a página para continuar.',
    codigo: (c: string) => `Código do erro: ${c}`,
    recarregar: 'Recarregar',
  },

  ambiente: {
    staging: 'Ambiente de testes',
    local: 'Ambiente local',
  },

  /* ── Painel ───────────────────────────────────────────────────────────── */
  dashboard: {
    seeAll: 'Ver todas',
    appointmentCount: (n: number) => (n === 1 ? '1 consulta' : `${n} consultas`),
    conversationsOpen: 'Conversas abertas',
    awaitingReply: 'Aguardando resposta',
    windowsClosing: 'Janelas fechando',
    appointmentsToday: 'Consultas hoje',
    activeClients: 'Clientes ativos',
    monthlyRevenue: 'Receita mensal',
    needAttention: 'Precisam de atenção',
    overduePayments: 'Cobranças em atraso',
    channelsHealthy: 'Números no ar',
    uniqueRecipients: 'Destinatários em 24 h',
    tier: 'Limite atual',
    oneNeedsAction: '1 cliente precisa de ação agora',
    manyNeedAction: (n: number) => `${n} clientes precisam de ação agora`,
    andMore: (n: number) => ` · e mais ${n}`,
    indicators: 'Indicadores',
    /* Rótulo CURTO: o tile tem uma linha, e a frase inteira quebrava em três e
       empurrava o número para baixo, desalinhando a fileira. O detalhe está no
       `Hint`, que agora é o hover. */
    clientsAttentionTitle: 'Clientes com pendência',
    clientsAttentionHint:
      'Conexão caída, sem cartão na conta da Meta do médico ou qualidade em queda. Enquanto isso dura, o cliente acha que você parou de responder.',
    windowsExpiringTitle: 'Janelas fechando',
    windowsExpiringHint: 'Passando de 24 h, só um template aprovado reabre a conversa.',
    conversationsWaiting: 'Conversas aguardando',
    activeClientsHint: 'Médicos com contrato em vigor',
    overdueHint: 'Mensalidade vencida e não paga. Esquecer de cobrar é receita que não volta.',
    waitingTitle: 'Conversas aguardando resposta',
    waitingSub: 'De todos os clientes, ordenadas pela janela que fecha primeiro',
    waitingEmpty: 'Nenhuma conversa esperando',
    waitingEmptyText: 'Toda mensagem recebida já foi respondida. A equipe está em dia.',
    waitingEmptyNoClients:
      'As conversas aparecem aqui quando o WhatsApp do primeiro cliente estiver conectado.',
    firstRunTitle: 'Comece pelo primeiro médico que você atende',
    firstRunText:
      'Cadastre o cliente, conecte o WhatsApp dele e as conversas dos pacientes passam a chegar aqui.',
    firstRunCta: 'Cadastrar primeiro cliente',
    todayTitle: 'Agenda de hoje',
    openAgenda: 'Abrir agenda',
    todayEmpty: 'Nenhuma consulta hoje',
    todayEmptyText: 'A agenda de hoje está livre. Bom momento para confirmar a semana.',
    windowClosed: 'janela fechada',
    windowLeft: (tempo: string) => `faltam ${tempo}`,
    openClients: 'Abrir clientes',
    openInbox: 'Abrir conversas',
    openTotalHint: (n: number) => `${n} conversas abertas no total`,
    revenueHint: (n: number) => `${n} clientes ativos na sua operação`,
  },

  /* ── Inbox ────────────────────────────────────────────────────────────── */
  inbox: {
    fichaAcoes: {
      marcar: 'Marcar',
      desmarcar: 'Desmarcar',
      nao: 'Não',
      serie: 'série',
      encaixe: 'encaixe',
      desmarcarSo: 'Desmarcar só esta consulta',
      desmarcarTodas: 'Desmarcar todas',
      desmarcarHora: (hora: string) => `Desmarcar ${hora}?`,
      desmarcarN: (n: number) => `Desmarcar as ${n} consultas?`,
    },
    tambemFalaCom: 'Também conversa com',
    marcarCom: 'Marcar com',
    desta: 'desta conversa',
    marcadaComOutro: (nome: string) =>
      `Consulta marcada com ${nome}. Ela aparece na agenda de ${nome}; avise o paciente pela conversa com esse médico.`,
    comOutrosMedicos: 'Com outros médicos',
    abrirFicha: 'Ficha',
    abrirFichaDica: 'Dados do paciente, convênio e dependentes',
    incluiDependentes: (n: number) =>
      n === 1 ? 'Uma delas é de dependente.' : `${n} delas são de dependentes.`,
    sumiu: 'Esta conversa não está disponível para você',
    sumiuTexto:
      'O médico pode ter saído da equipe, ou o link está errado. Escolha outra conversa na lista.',
    foco: {
      daCasa: 'Da sua operação',
      daDona: (nome: string) => `De ${nome}`,
      foraDoAlcance: (n: number) =>
        n === 1
          ? '1 médico do seu foco não está mais compartilhado.'
          : `${n} médicos do seu foco não estão mais compartilhados.`,
      explica: 'Você só recebe aviso das conversas dos médicos marcados.',
      soEste: (nome: string) => `Só ${nome}`,
      tirarSoEste: 'Voltar para o seu foco',
    },
    da: (nome: string) => `de ${nome}`,
    com: (nome: string) => `com ${nome}`,
    voce: 'Você',
    assumir: 'Assumir',
    assumirDe: (nome: string) => `Assumir de ${nome}`,
    soltar: 'Soltar',
    assumirDica: 'Fica com você: as outras pessoas veem que esta conversa tem dono.',
    escrevendo: (nomes: readonly string[]) =>
      nomes.length === 1 ? `${nomes[0]} está escrevendo` : `${nomes.join(', ')} estão escrevendo`,
    searchPlaceholder: 'Buscar por nome, telefone ou médico',
    filters: {
      all: 'Todas',
      awaiting: 'Aguardando',
      urgent: 'Urgentes',
      resolved: 'Resolvidas',
      mine: 'Minhas',
      unassigned: 'Sem ninguém',
    },
    allClients: 'Todos os clientes',
    /* Curto para o BOTAO; dentro do painel o item continua explicito. */
    allClientsShort: 'Todos',
    clientsChosen: (n: number) => `${n} clientes`,
    emptyTitle: 'Nada por aqui',
    emptyText:
      'Nenhuma conversa combina com esse filtro. Tente "Todas" ou volte para todos os clientes.',
    pickTitle: 'Escolha uma conversa',
    pickText:
      'A lista à esquerda junta os pacientes de todos os seus clientes, com a etiqueta de quem é cada um.',
    naoSuportada: 'O WhatsApp não repassa este tipo de mensagem. Veja no celular do consultório.',
    noMessages: 'Sem mensagens',
    waitingFor: (tempo: string) => `esperando há ${tempo}`,
    windowClosed: 'janela fechada',
    windowClosingIn: (tempo: string) => `fecha em ${tempo}`,
    resolve: 'Resolver',
    reopen: 'Reabrir',
    reopenHint: 'Voltar esta conversa para a fila. Ela deixa de estar resolvida.',
    reply: 'Resposta',
    internalNote: 'Nota interna',
    send: 'Enviar',
    replyTo: (paciente: string, cliente: string) => `Responder ${paciente} por ${cliente}`,
    failed: 'Falhou',
    internalNoteLabel: 'Nota interna',
    sentFromPhone: 'enviado pelo celular do médico',
    attach: 'Anexar arquivo',
    download: 'Baixar',
    mediaExpires: (data: string) => `disponível até ${data}`,
    tagAdd: 'Tag',
    tagSearch: 'Buscar ou criar…',
    tagCreate: (nome: string) => `Criar “${nome}”`,
    tagSearchOnly: 'Buscar…',
    tagSoDaDona: 'Nenhuma etiqueta com esse nome. Etiqueta nova, só quem trouxe o médico cria.',
    tagFirst: 'Digite para criar sua primeira tag.',
    tagAllUsed: 'Todas as tags já estão nesta conversa.',
    panelToggle: 'Ficha do contato',
    conversation: 'Conversa',
    conversationEmpty: 'Conversa sem mensagens',
    conversationEmptyText:
      'Ninguém escreveu ainda. Para começar, mande um template aprovado aqui embaixo, ou espere o paciente escrever.',
    filterByClient: 'Filtrar por cliente',
    contactCard: 'Ficha do contato',
    collapseCard: 'Recolher a ficha do contato',
    expandCard: 'Abrir a ficha do contato',
    client: 'Cliente',
    assignee: 'Atendente',
    noAssignee: 'Sem responsável',
    lastMessage: 'Última mensagem',
    saveNote: 'Salvar nota',
    blockedByWindow: 'Resposta livre bloqueada pela janela de 24 h',
    windowTooltip: 'Janela de 24 h fechada: só um template aprovado reabre a conversa',
    noteTooltip: 'Nota interna: nunca vai para o WhatsApp do paciente',
    window: {
      closedTitle: 'Janela fechada: só template',
      closedReason:
        'O paciente não escreve há mais de 24 h, e a Meta só deixa reabrir com um template aprovado. Se o número é Coexistência, também dá para responder pelo WhatsApp Business do celular.',
      openTitle: 'Janela aberta',
      freeTitle: 'Ponto de entrada gratuito',
      remaining: (tempo: string) => `, faltam ${tempo}`,
      freeSub: 'Conversa iniciada por anúncio: 72 h e tudo nela é gratuito.',
      openSub: (hora: string) => `Resposta livre liberada até ${hora}.`,
      replyNow: 'responder agora',
    },
    today: 'Hoje',
    yesterday: 'Ontem',
    hint: {
      comVoce: 'Você assumiu esta conversa',
      comAlguem: (nome: string) => `${nome} assumiu esta conversa`,
      resolved: 'Conversa resolvida: nada pendente aqui',
      locked: 'Janela de 24 h fechada: só um template aprovado reabre a conversa',
      open: 'Conversa aberta: resposta livre liberada',
      needsReply: 'O paciente falou e ninguém respondeu ainda',
      unread: (n: number) => (n === 1 ? '1 mensagem não lida' : `${n} mensagens não lidas`),
      waiting: (tempo: string) => `O paciente espera resposta há ${tempo}`,
      closingIn: (tempo: string) => `A janela de 24 h fecha em ${tempo}. Depois disso, só template`,
      tag: (nome: string) => `Etiqueta: ${nome}`,
      moreTags: (n: number) => `Mais ${n} etiqueta(s) nesta conversa`,
      stamp: (quando: string) => `Última mensagem em ${quando}`,
    },
    windowOpenFlag: 'janela aberta',
    windowClosingIn2: (tempo: string) => `fecha em ${tempo}`,
    windowTitle: 'Janela de 24 h',
    windowOpenFor: (tempo: string) => `aberta · faltam ${tempo}`,
    windowShut: 'fechada',
    nextAppointment: 'Próxima consulta',
    noAppointment: 'Sem consulta agendada.',
    telehealth: 'Teleconsulta',
    clientTag: (nome: string) => `Cliente: ${nome}`,
  },

  /* ── Clientes ─────────────────────────────────────────────────────────── */
  clients: {
    newClient: 'Novo cliente',
    emptyTitle: 'Nenhum cliente cadastrado',
    emptyText:
      'Cadastre o primeiro médico que você atende para começar a receber as conversas no inbox.',
    columns: {
      client: 'Cliente',
      connection: 'Conexão',
      conversations: 'Conversas abertas agora',
      patientsMonth: 'Pacientes neste mês',
      billing: 'Cobrança',
      openClient: 'Abrir cliente',
    },
    searchPlaceholder: 'Buscar por nome ou especialidade',
    noMatchTitle: 'Nenhum cliente encontrado',
    noMatchText: (termo: string) => `Nada corresponde a “${termo}”. Tente outro nome.`,
    revenueFooter: (n: number) =>
      n === 1 ? 'Receita mensal · 1 cliente ativo' : `Receita mensal · ${n} clientes ativos`,
    onAir: 'No ar',
    onAirHint: (n: number) =>
      n > 1
        ? `Os ${n} números deste cliente estão conectados e enviando normalmente.`
        : 'O WhatsApp deste cliente está conectado e enviando normalmente.',
    numbersOnAir: (n: number) => `${n} números no ar`,
    overdue: 'EM ATRASO',
    onTime: 'em dia',
    pending: 'em aberto',
    noDay: 'sem dia',
    dayOf: (d: number) => `dia ${d}`,
    /* O ponto da coluna de cobrança não tem texto — o hover é que explica, e por
       isso ele precisa dizer a frase inteira, não a etiqueta curta. */
    overdueHint: 'Mensalidade em atraso',
    onTimeHint: 'Em dia, nada a receber',
    pendingHint: 'Mensalidade em aberto, ainda no prazo',
    pendingHintOn: (d: number) => `Mensalidade em aberto, vence dia ${d}`,
    tabs: {
      overview: 'Visão geral',
      patients: 'Pacientes',
      templates: 'Templates',
      billing: 'Cobrança',
      activity: 'Atividade',
    },
    contact: 'Contato',
    faleCom: (nome: string) => `Só ${nome} resolve isso: avise.`,
    daEquipe: 'Da equipe',
    pelaEquipe: (nome: string) => `Pela equipe, de ${nome}`,
    soDaDona: (nome: string) =>
      `O número, o cadastro e a cobrança deste médico ficam com ${nome}. Você atende as conversas e a agenda dele.`,
    phone: 'Telefone',
    since: 'Cliente desde',
    seeConversations: 'Ver conversas',
    connection: 'Conexão',
    connectionSub: 'O WhatsApp deste médico. É o que quebra sozinho e você precisa restabelecer.',
    noDoctorsTitle: 'Nenhum médico cadastrado',
    noDoctorsText: 'Cadastre o médico deste cliente para conectar o WhatsApp dele.',
    noNumber: 'Sem número conectado',
    noNumberNote:
      'Este médico ainda não recebe nem responde pelo Zenda. Enquanto não conectar o WhatsApp dele, nenhuma conversa dele chega no seu inbox.',
    connectWhatsapp: 'Conectar WhatsApp',
    reconnect: 'Reconectar',
    channelType: 'Tipo de canal',
    coexistence: 'Coexistência',
    classicChannel: 'Canal clássico',
    quality: 'Qualidade',
    metaBilling: 'Cobrança na Meta',
    tier: 'Tier de envio',
    perDay: '/ dia',
    neverSynced: 'Qualidade e limite ainda não conferidos com a Meta',
    syncedAt: (quando: string) => `Conferido com a Meta ${quando}`,
    syncNow: 'Conferir agora',
    disconnect: 'Desconectar número',
    disconnectConfirm: 'Confirmar desconexão',
    disconnecting: 'Desconectando…',
    disconnectHelp:
      'O número deixa de enviar e receber pela Zenda, e as conversas ficam. Para conectar de novo, gere um novo link.',
    billingByZenda: 'Pela Zenda',
    noTierLimit: 'Sem teto',
    appSignal: 'Sinal do app',
    notApplicable: 'Não se aplica',
    noSignal: 'Sem sinal registrado',
    activeToday: 'Ativo hoje',
    daysAgo: (n: number) => `Há ${n} ${n === 1 ? 'dia' : 'dias'}`,
    billingTitle: 'Cobrança',
    billingSub: 'Lembrete de cobrança. O Zenda não emite nem cobra nada: o controle é seu.',
    noBilling: 'Nenhuma cobrança registrada ainda.',
    markPaid: 'Marcar como recebido',
    undoPaid: 'Desfazer',
    received: 'Recebido',
    dueOn: (data: string) => `vence ${data}`,
    paidOn: (data: string) => `pago ${data}`,
    volumeTitle: 'Volume',
    volumeSub: 'O trabalho que este cliente dá.',
    patientsTotal: 'Pacientes no total',
    trendFlat: 'Igual ao mês passado',
    trendPct: (p: number, subiu: boolean) =>
      subiu ? `+${p}% vs. mês passado` : `-${p}% vs. mês passado`,
    trendFromZero: (n: number) => `${n} neste mês, e o mês passado teve zero`,
    trendNoData: 'Sem movimento nos dois meses',
    trendFoot: (antes: number, agora: number) =>
      `Mês passado ${antes} · este mês ${agora}, ainda correndo`,
    patientsMonth: 'Pacientes no mês',
    openConversations: 'Conversas abertas',
    appointmentsMonth: 'Consultas no mês',
    sectionsAria: 'Seções do cliente',
    openError: 'Não foi possível abrir este cliente',
    openBadge: 'Em aberto',
  },

  /* ── Formulário de cliente ────────────────────────────────────────────── */
  clientForm: {
    newTitle: 'Novo cliente',
    newSubtitle: 'Um médico que você atende. Depois de cadastrar, você conecta o WhatsApp dele.',
    editTitle: (nome: string) => `Editar ${nome}`,
    editSubtitle: 'O WhatsApp do médico se gerencia na página do cliente.',
    submitNew: 'Cadastrar cliente',
    submitEdit: 'Salvar alterações',
    name: 'Nome do cliente',
    namePlaceholder: 'Dra. Beatriz Nogueira',
    specialtyPlaceholder: 'Dermatologia',
    nameRequired: 'Dê um nome ao cliente: é como ele vai aparecer nas conversas.',
    specialty: 'Especialidade',
    // O CRM ja vem com a sigla do conselho no valor ("CRM-SP 92.771"), entao o rotulo
    // nao repete: "CRM CRM-SP" foi o resultado da primeira tentativa.
    crm: 'CRM',
    contactName: 'Pessoa de contato',
    contactHint: 'Quem te chama quando precisa',
    contactPhone: 'Telefone do contato',
    fee: 'Quanto você cobra por mês (R$)',
    day: 'Dia da cobrança',
    dayHint:
      'Só um lembrete para você não perder a data. O Zenda não emite nem cobra nada. Até o dia 28, porque fevereiro.',
    dayInvalid: (max: number) =>
      `O dia da cobrança precisa estar entre 1 e ${max}, por causa de fevereiro.`,
    paymentOwner: 'Quem paga a Meta por este cliente',
    ownerClient: 'Ele mesmo',
    ownerAttendant: 'Você',
    ownerHintClient:
      'O médico anexa o cartão dele à própria conta na Meta. É o caminho padrão, e o único em que você não carrega o risco de crédito dele.',
    ownerHintZenda:
      'A Zenda, pela Gupshup. O médico não cadastra cartão, e você não paga a Meta por ele.',
    agendaSource: 'Agenda deste cliente',
    agendaZenda: 'Zenda',
    agendaDoctoralia: 'Doctoralia',
    agendaHintZenda:
      'A agenda é nossa: nós garantimos que dois pacientes não caiam no mesmo horário.',
    agendaHintDoctoralia:
      'A fonte de verdade passa a ser o Doctoralia, e o Zenda vira a interface dele. Marcar aqui reserva lá primeiro, e o que o paciente marcar lá aparece aqui sozinho. Da Zenda fica só a disponibilidade, dizendo quando pode marcar.',
    ownerHintAttendant:
      'Ele te adiciona ao portfólio dele como administradora e você anexa o seu cartão, embutindo o custo na mensalidade. A conta e o número continuam sendo dele.',
    status: 'Situação',
    statusActive: 'Ativo',
    statusPaused: 'Pausado',
    statusChurned: 'Encerrado',
    statusHintActive: 'Ativo é o normal: entra na receita mensal e nos alertas do painel.',
    statusHintPaused:
      'Pausado continua na lista, mas para de gerar cobrança. Para quem tirou férias.',
    statusHintChurned:
      'Encerrado sai da receita mensal e do painel. As conversas e o histórico continuam aqui.',
  },

  /* ── Pacientes ────────────────────────────────────────────────────────── */
  patients: {
    title: 'Pacientes',
    subtitle:
      'Quem falou com este cliente pelo WhatsApp e quem você cadastrou. O número identifica a pessoa: quando ela escrever, a mensagem cai no mesmo cadastro.',
    searchPlaceholder: 'Buscar por nome ou número',
    searchAria: 'Buscar paciente',
    dueFilter: 'A voltar',
    emptyTitle: 'Nenhum paciente ainda',
    emptyText:
      'Cadastre um paciente, ou espere: quem mandar mensagem para o WhatsApp deste cliente aparece aqui sozinho.',
    notFoundTitle: 'Nada encontrado',
    notFoundText: 'Tente outro termo, ou desligue o filtro de retorno.',
    columns: {
      patient: 'Paciente',
      nextAppointment: 'Próxima consulta',
      lastConversation: 'Última conversa',
      appointments: 'Consultas',
      openRecord: 'Abrir ficha',
    },
    noReturn: 'sem retorno',
    conversations: 'Conversas',
    appointments: 'Consultas',
    patientSince: 'Paciente desde',
    notesTitle: 'Anotações',
    notesPlaceholder: 'Prefere manhã. Vem sempre acompanhada da filha. Convênio X.',
    notesHint:
      'Só você e sua equipe veem isto. Não aparece para o paciente e nunca vai numa mensagem. E a anotação de um cliente não aparece na tela de outro.',
    birthDate: 'Nascimento',
    appointmentsNone: 'Nenhuma consulta registrada.',
    conversationsNone: 'Ainda não conversou pelo WhatsApp.',
    consultation: 'Consulta',
    openConversation: 'Abrir conversa',
    sendMessage: 'Enviar mensagem',
    autoSaved: 'As alterações salvam sozinhas.',
    saved: 'Salvo',
    intervalInvalid: 'O intervalo precisa ser maior que zero',
    patientFallback: 'Paciente',
    newPatient: 'Novo paciente',
    neverTalked: 'Ainda não conversou',
    novo: {
      title: 'Novo paciente',
      subtitle:
        'O WhatsApp identifica a pessoa. Quando ela escrever, a mensagem cai neste cadastro.',
      name: 'Nome',
      namePlaceholder: 'Maria da Silva',
      phone: 'WhatsApp',
      phonePlaceholder: '(11) 98765-4321',
      nameRequired: 'Informe o nome do paciente.',
      phoneRequired: 'Informe o WhatsApp do paciente.',
      phoneHint: 'Com DDD. Fixo ou celular, com ou sem o 9: o sistema reconhece as duas formas.',
      phoneOk: (n: string) =>
        `Vai ficar como ${n}. Quando esse número escrever no WhatsApp, a mensagem cai nesta ficha.`,
      jaCadastrado: (nome: string) =>
        `Este número já é de ${nome}. Cadastrar abre a ficha que já existe, sem duplicar.`,
      submit: 'Cadastrar',
      alreadyLinked: 'Este número já é paciente deste cliente. Abrimos a ficha dele.',
    },
    ficha: {
      grupos: {
        identificacao: 'Identificação',
        contato: 'Contato',
        endereco: 'Endereço',
        convenio: 'Convênio',
        origem: 'Origem',
      },
      campos: {
        nome: 'Nome',
        whatsapp: 'WhatsApp',
        socialName: 'Nome social',
        sex: 'Sexo',
        cpf: 'CPF',
        email: 'E-mail',
        phoneSecondary: 'Outro telefone',
        cep: 'CEP',
        street: 'Rua',
        streetNumber: 'Número',
        complement: 'Complemento',
        district: 'Bairro',
        city: 'Cidade',
        uf: 'UF',
        insurer: 'Operadora',
        insurancePlan: 'Plano',
        insuranceCardNumber: 'Carteirinha',
        insuranceValidUntil: 'Validade',
        referralSource: 'Como conheceu o médico',
      },
      sexos: { F: 'Feminino', M: 'Masculino', I: 'Outro' },
      naoInformado: 'Não informado',
      nomeObrigatorio: 'O nome não pode ficar vazio.',
      telefoneObrigatorio: 'O WhatsApp é o que identifica o paciente e não pode ficar vazio.',
      telefoneTravado: 'O paciente já conversou por este número, e ele não muda mais.',
    },
    dependentes: {
      title: 'Dependentes',
      hint: 'Quem é atendido sem ser o dono do WhatsApp, como um filho. As mensagens continuam indo para o responsável.',
      none: 'Nenhum dependente.',
      add: 'Adicionar dependente',
      nameRequired: 'Informe o nome do dependente.',
      name: 'Nome',
      relationship: 'Parentesco',
      birthDate: 'Nascimento',
      save: 'Salvar',
      edit: 'Editar',
      archive: 'Arquivar',
      unarchive: 'Reativar',
      archived: 'Arquivado',
      archiveWarning: (n: number) =>
        n === 1 ? 'Tem 1 consulta marcada.' : `Tem ${n} consultas marcadas.`,
      parentescos: {
        filho: 'Filho ou filha',
        enteado: 'Enteado ou enteada',
        neto: 'Neto ou neta',
        sobrinho: 'Sobrinho ou sobrinha',
        tutelado: 'Sob tutela',
        conjuge: 'Cônjuge',
        pai_mae: 'Pai ou mãe',
        outro: 'Outro',
      },
      paraQuem: 'Para quem',
      oProprio: (nome: string) => `${nome} (o próprio paciente)`,
      deQuem: (dependente: string, responsavel: string) => `${dependente} · resp. ${responsavel}`,
      proximaDe: (nome: string) => `de ${nome}`,
      naLista: (nomes: string) => `Dependentes: ${nomes}`,
    },
    optIn: {
      title: 'Mensagens pelo WhatsApp',
      declare: 'O paciente autorizou receber mensagens deste médico pelo WhatsApp',
      method: 'Como autorizou',
      methods: {
        presencial: 'Pessoalmente',
        telefone: 'Por telefone',
        papel: 'Por escrito',
        outro: 'Outro meio',
      },
      hint: 'Sem essa autorização, a primeira mensagem só pode ir para quem já escreveu.',
      precisa:
        'Para mandar a primeira mensagem, registre em "Mensagens pelo WhatsApp" que o paciente autorizou.',
      given: (quando: string) => `Autorizou em ${quando}`,
      none: 'Nenhuma autorização registrada.',
      register: 'Registrar autorização',
      remove: 'Retirar',
    },
  },

  /* ── Agenda ───────────────────────────────────────────────────────────── */
  agenda: {
    dias: {
      letra: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
      curto: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
      titulo: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    } as { letra: readonly string[]; curto: readonly string[]; titulo: readonly string[] },
    deOutrosMedicos: 'Pacientes de outros médicos (passam a ser deste também)',
    consulta: {
      abrirConversa: 'Abrir a conversa',
      abrirFicha: 'Abrir a ficha do paciente',
      teleconsulta: 'Teleconsulta',
      encaixe: 'encaixe',
      marcadaPor: (nome: string) => `Marcada por ${nome}`,
      alteradaPor: (nome: string) => `alterada por ${nome}`,
      naPlataforma:
        'Esta consulta está no Doctoralia. Remarcar, confirmar, registrar a presença e desmarcar valem lá também.',
      remarcar: 'Remarcar',
      novaData: 'Nova data',
      novaHora: 'Novo horário',
      salvarRemarcacao: 'Remarcar para este horário',
      voltar: 'Voltar',
      confirmar: 'Paciente confirmou',
      veio: 'Veio',
      faltou: 'Faltou',
      desmarcar: 'Desmarcar',
      desmarcarPergunta: 'Desmarcar esta consulta?',
    },
    /* A porta: a agenda é de um médico, e nunca a soma de vários. */
    chooseTitle: 'De quem é a agenda?',
    chooseText: 'Escolha um cliente para ver os dias, os horários e o que está marcado.',
    noDoctor: 'Sem médico',
    noDoctorText:
      'Este cliente não tem médico cadastrado, e a agenda é de um médico. Cadastre um na página do cliente.',
    periods: {
      madrugada: 'Madrugada',
      manha: 'Manhã',
      tarde: 'Tarde',
      noite: 'Noite',
    },
    noService: 'sem atendimento',
    previousDay: 'Dia anterior',
    nextDay: 'Próximo dia',
    secoes: 'Seções da agenda',
    previousWeek: 'Semana anterior',
    nextWeek: 'Próxima semana',
    jumpToDate: 'Ir para uma data',
    backToClients: 'Trocar de cliente',

    today: 'Hoje',
    week: 'Semana',
    tabCalendar: 'Agenda',
    month: 'Mês',
    visao: 'Visão da agenda',
    previousMonth: 'Mês anterior',
    nextMonth: 'Próximo mês',
    verDia: 'Ver o dia',
    maisN: (n: number) => `+${n} mais`,
    tabDay: 'Dia',
    tabAvailability: 'Disponibilidade',
    tabIntegration: 'Integração',

    telehealth: 'Teleconsulta',
    free: 'livre',
    extra: 'extra',
    closedNoRoutine: 'não atende',
    closedBlocked: 'bloqueado',
    booked: (n: number) => (n === 1 ? '1 marcada' : `${n} marcadas`),

    closedDayTitle: 'O médico não atende neste dia',
    closedDayText: 'A rotina não abre nenhum horário aqui. Uma exceção "atende extra" abre.',
    blockedDayTitle: 'Dia bloqueado',
    sourceZendaTitle: 'A agenda deste cliente é a Zenda',
    sourceZendaText:
      'Não há nada a sincronizar: nós somos a agenda, e é a nossa trava que garante que dois pacientes não caiam no mesmo horário. Para usar o Doctoralia, mude na ficha do cliente.',
    noLinksTitle: 'A agenda é o Doctoralia, e nenhum calendário está vinculado',
    noLinksText:
      'Neste estado não é possível marcar por aqui: a reserva nasce lá, e sem calendário vinculado não há onde reservar. Vincule abaixo, ou volte a agenda deste cliente para a Zenda na ficha dele.',
    linksTitle: 'Calendários vinculados',
    syncFailed: 'falhou',
    willRetry: 'vai tentar de novo sozinho',
    neverSynced: 'ainda não sincronizou',
    syncedAt: 'sincronizado',

    /* ── A janela de configuração ── */
    configOpen: 'Configurar',
    configTitle: 'Sistema de agenda',
    configSystem: 'Qual sistema é a agenda deste cliente',
    configSystemHint:
      'Em plataforma de terceiro, a fonte de verdade passa a ser lá e o Zenda vira a interface. Da disponibilidade daqui sobra o guardrail de quando pode marcar.',
    configZendaDesc:
      'A agenda é nossa, e é a nossa trava que garante que dois pacientes não caiam no mesmo horário.',
    configPlatformDesc:
      'Os dois lados andam juntos: o que você marca aqui é criado lá primeiro, e o que o paciente marcar lá aparece aqui sozinho.',
    configConnection: 'A conexão',
    configSimulated: 'Simulado',
    configLive: 'Real',
    configSimulatedHint:
      'Não fala com ninguém: serve para ver o fluxo funcionando antes de a parceria estar aprovada.',
    configLiveHint:
      'Fala com a plataforma de verdade. O acesso é concedido por parceria aprovada e autorizado cliente por cliente.',
    configKeepSecret: 'Em branco mantem a credencial atual',
    configRedirected:
      'Atenção: o modo Real está desviado e NÃO fala com a plataforma. As chamadas vão para',
    configRedirectedBadge: 'Real, desviado',
    configRead: 'Ler calendários',
    configReading: 'Lendo…',
    configTelehealth: 'Teleconsulta (calendário online)',
    configMapping: 'Qual local reflete qual calendário',
    configMappingHint:
      'Lá o calendário É o endereço, e nada garante que os nomes batam. Sem dizer qual reflete qual, a grade de um endereço iria para o calendário de outro.',
    configDefault: 'padrão',
    configNoService: 'Nada configurado na plataforma para este campo, e sem valor não há reserva.',
    configDefaults: 'Padrões da reserva',
    configHere: 'Aqui (Zenda)',
    configThere: 'Lá',
    configNoSync: 'Não sincronizar',
    configDisabled: 'desativado lá',
    configDisabledWarn: 'Esse calendário está desativado na plataforma e vai recusar as reservas.',
    configNoCalendars: 'Essa conta não tem nenhum calendário. Confira no painel da plataforma.',
    configNoDoctor:
      'Este cliente não tem médico cadastrado, e o calendário do outro lado é de um médico.',
    linksHint:
      'Um calendário por endereço, porque do outro lado o calendário É o endereço. A sincronia é em tempo real nos dois sentidos e não tem botão: o que aparece aqui é se algo falhou.',
    mirrorOf: 'Doctoralia',
    pushShow: 'URL de push',
    pushHint:
      'Cole no painel da plataforma, no campo de notificação. Sem ela, a reserva feita lá só aparece aqui na leitura seguinte, que pode levar um minuto. Ela carrega um segredo: trate como senha.',
    pushMissing: 'Este vínculo não tem URL de push. Salve a configuração de novo para gerar uma.',
    mirrorHint:
      'A agenda deste médico é o Doctoralia. O que você vê aqui é o que existe lá, e marcar aqui reserva lá primeiro.',
  },

  /* ── Conexão do WhatsApp ──────────────────────────────────────────────── */
  /* ── Formulário de template ───────────────────────────────────────────────
     Estava inteiro cravado em português, e é a tela que prova
     `whatsapp_business_management` no App Review: quem abrisse em inglês via
     metade do produto na outra língua. */
  templatesTab: {
    title: 'Templates de mensagem',
    subtitle:
      'Mensagem pronta aprovada pela Meta. É o único jeito de escrever para quem não fala há mais de 24 h, como num lembrete de consulta. Cada template é da conta deste médico.',
    newTemplate: 'Novo template',
    newTemplateSubtitle: (cliente: string) =>
      `Vai para análise da Meta na conta de ${cliente}. A resposta costuma levar minutos.`,
    colMessage: 'Mensagem',
    colName: 'Nome',
    colLanguage: 'Idioma',
    colCategory: 'Categoria',
    colStatus: 'Estado',
    open: 'Abrir',
    sentText: 'Texto enviado à Meta',
    sentExamples: 'Exemplos enviados',
    rejectedReason: (motivo: string) => `Motivo da recusa informado pela Meta: ${motivo}`,
    unavailableTitle: 'Templates indisponíveis',
    emptyTitle: 'Nenhum template ainda',
    emptyText:
      'Sem template aprovado, você só responde quem escreveu nas últimas 24 h. Comece por um de utilidade. Ex.: "Olá {{1}}, sua consulta é amanhã às {{2}}. Confirma?"',
    status: {
      approved: 'Aprovado',
      approvedHelp: 'Pode ser enviado, inclusive fora da janela de 24 h.',
      pending: 'Em análise',
      pendingHelp: 'A Meta ainda está revisando. Costuma levar minutos, mas pode levar horas.',
      rejected: 'Recusado',
      rejectedHelp: 'A Meta recusou. Corrija o texto e crie de novo: o recusado não volta a valer.',
      paused: 'Pausado',
      pausedHelp: 'Pausado por qualidade baixa: muitos pacientes bloquearam ou denunciaram.',
      unknownHelp: 'Estado informado pela Meta.',
    },
    categories: {
      UTILITY: 'Utilidade',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Autenticação',
    },
    reasons: {
      sem_canal:
        'Este cliente ainda não tem WhatsApp conectado. Os templates existem dentro da conta da Meta dele, então só aparecem depois da conexão.',
      sem_credencial:
        'A credencial deste canal não está disponível. Reconecte o WhatsApp do médico para voltar a ler os templates.',
      meta_indisponivel: 'Não foi possível falar com a Meta agora. Tente de novo em instantes.',
      sem_meta:
        'Este ambiente não está ligado à Meta, então não há templates para ler nem criar aqui. Em staging e produção eles aparecem normalmente.',
    },
  },

  templateForm: {
    fromModel: 'Começar de um modelo',
    yourModels: 'Seus modelos, de Configurações → Modelos de template.',
    internalName: 'Nome interno',
    internalNameHint: 'Só o time vê. O paciente nunca lê este nome.',
    internalNamePlaceholder: 'lembrete_consulta_24h',
    message: 'Mensagem',
    messageHint: (n: number) => `Use {{1}}, {{2}} para o que muda a cada paciente. ${n}/1024`,
    messagePlaceholder: 'Olá {{1}}, seu horário está agendado para {{2}} às {{3}}.',
    examples: 'Exemplo de cada variável',
    examplesHint:
      'A Meta usa para revisar. Use exemplos de mentira, como "Ana" e "amanhã às 9h": o texto aparece na tela de bloqueio do paciente.',
    exampleFirst: 'Ana',
    exampleAria: (n: number) => `Exemplo da variável ${n}`,
    onNotification: 'Na notificação',
    onNotificationHint:
      'O paciente lê isto sem desbloquear, e o aparelho costuma ser compartilhado.',
    previewEmpty: 'A mensagem aparece aqui.',
    onConversation: 'Na conversa',
    zendaRule: 'regra do Zenda',
    categoryPre: 'Enviado como',
    categoryUtility: 'Utilidade',
    categoryPost:
      'mas quem decide é a Meta, pelo texto. Se parecer propaganda, ela muda para Marketing, que custa cerca de 9 vezes mais por mensagem (R$ 0,32 contra R$ 0,035).',
    sending: 'Enviando…',
    submit: 'Enviar para análise',
    /* Chaveado pelo `regra` do kernel, que é estável. Regra sem tradução aqui cai
       na mensagem em pt-BR do kernel, então nenhuma some da tela. */
    rules: {
      nome: () =>
        'O nome aceita só letras minúsculas, números e underline. Ex.: lembrete_consulta_24h',
      corpo_vazio: () => 'Escreva o texto da mensagem.',
      corpo_tamanho: (d: Vars) =>
        `O texto passou de ${d['limite']} caracteres. Corte ${d['excesso']}.`,
      rodape_tamanho: () => 'O rodapé aceita até 60 caracteres.',
      cabecalho_tamanho: () => 'O cabeçalho aceita até 60 caracteres.',
      chaves: () => 'Há uma chave sozinha no texto. Variável se escreve exatamente assim: {{1}}',
      numeracao: (d: Vars) =>
        `As variáveis precisam ser 1, 2, 3… sem pular. Estão: ${d['achadas']}`,
      variavel_no_inicio: () =>
        'O texto não pode começar com variável. Comece com uma palavra, por exemplo: "Olá {{1}}".',
      variavel_no_fim: () =>
        'O texto não pode terminar com variável. Feche com uma palavra ou pontuação.',
      exemplos: (d: Vars) => `Preencha um exemplo para cada variável. Faltam ${d['faltando']}.`,
      muita_variavel: () =>
        'Quase tudo no texto é variável. A Meta costuma recusar mensagens assim: escreva mais texto fixo.',
      dado_clinico: (d: Vars) =>
        d['onde'] === 'nome'
          ? `Remova "${d['termo']}" no nome. O nome não chega ao paciente, mas viaja para a Meta e é lido por revisor humano, e entrega a especialidade do médico igual.`
          : `Remova "${d['termo']}" no texto. O texto aparece na tela de bloqueio do celular do paciente, antes de ele desbloquear, e o aparelho é compartilhado com família e trabalho.`,
      tom_promocional: () =>
        'Tom de promoção faz a Meta mudar o template para Marketing, que custa cerca de 9 vezes mais por mensagem. Troque palavras como "promoção" ou "desconto" por um aviso direto.',
    },
  },

  connect: {
    titleConnect: (medico: string) => `Conectar o WhatsApp de ${medico}`,
    titleReconnect: (medico: string) => `Reconectar o WhatsApp de ${medico}`,
    subtitleReconnect:
      'A conexão caiu. Restabelecer exige que o médico reautorize pelo aparelho dele.',
    subtitleClient:
      'O número é do médico e continua sendo dele, e a conta na Meta também. Você conduz, ele autoriza.',
    subtitleAttendant:
      'O número e a conta continuam sendo dele. Ele te adiciona como administradora ao final e VOCÊ anexa o seu cartão.',
    start: 'Iniciar conexão',
    startReconnect: 'Reconectar agora',
    starting: 'Iniciando…',
    whatHappens: 'Passo a passo do que o médico vai fazer',
    custosTitulo: 'Cartão, janela de 24 horas e quanto a Meta cobra',
    simulated: 'Conexão simulada.',
    testNumber: 'Número de teste da Meta.',
    openMeta: 'Abrir o fluxo da Meta',
    linkTitle: 'O médico está em outro lugar?',
    linkHelp:
      'Gere um link e mande para ele. Ele abre no computador dele, entra com a conta Meta dele e autoriza. Vale 72 horas e serve uma vez só.',
    linkCreate: 'Gerar link para o médico',
    linkCreating: 'Gerando…',
    linkCopy: 'Copiar link',
    linkCopied: 'Copiado',
    linkExpires: (quando: string) => `Vale até ${quando}. Gerar outro invalida este.`,
    metaRunning: 'Aguardando a Meta…',
    metaStep: (passo: string) => `Na janela da Meta, passo: ${passo}`,
    metaDone: 'Número conectado.',
    metaHalfDone: 'Conectado, mas sem webhook.',
    checklistTitle: 'Antes de ligar para o médico, confirme:',
    sendBefore: 'Mande isto para o médico antes de ligar',
    openWhatsapp: 'Abrir no WhatsApp',
    copyBlocked: 'O navegador bloqueou a cópia. Selecione o texto abaixo e copie na mão.',
    noPhone:
      'Sem telefone de contato cadastrado neste cliente, por isso não dá para abrir a conversa direto. Copie o texto ou preencha o telefone no cadastro.',
    metaCardHelp: 'Passo a passo da Meta para cadastrar o cartão',
    metaAddPeople: 'Como ele te adiciona ao portfólio dele',
    metaBilling: 'Ir direto para as configurações de cobrança',
    whyCardTitle: 'Se ele perguntar por que o cartão é dele',
    whyAccessTitle: 'Se ele perguntar por que você precisa de acesso',
    whyCard: [
      'A conta da Meta fica no CNPJ dele, e o número de WhatsApp continua sendo dele: se um dia sair, sai com o número.',
      'Ele vê o próprio gasto a qualquer momento, sem intermediário e sem markup.',
      'Você não cobra por mensagem: cobra pelo seu serviço, que é outra conta.',
      'É regra da Meta para o nosso tipo de parceria, não uma escolha nossa: sem cartão na conta dele, o número conecta e não envia.',
    ],
    whyAccess: [
      'A conta e o número continuam dele, no CNPJ dele. Você entra como administradora, não como dona.',
      'Ele te remove sozinho, a qualquer momento, e fica com tudo. A Meta nem permite que a gente restrinja o acesso dele.',
      'Sem esse acesso você não consegue anexar o seu cartão, e sem cartão o número conecta e não envia.',
      'De quebra: display name, qualidade e pendência de política passam a se resolver com você, sem precisar chamar ele toda vez.',
    ],
    semCartao: {
      subtitle:
        'O número e a conta continuam do médico. Ele autoriza pelo link e não cadastra cartão: a Zenda paga as mensagens.',
      custosTitulo: 'Janela de 24 horas e quanto a Meta cobra',
      linkHelp:
        'Mande o link para o médico. Ele vê um passo a passo curto e conecta pelo celular ou computador, em uns 5 minutos. Vale 72 horas e serve uma vez.',
      conferir: 'Conferir se o médico terminou',
      conferindo: 'Conferindo…',
      conferido: {
        ativado: 'Número conectado. As conversas já chegam na Zenda.',
        ja_ativo: 'Número conectado. As conversas já chegam na Zenda.',
        pendente: 'O médico ainda não terminou. Confira de novo quando ele avisar.',
        parado:
          'O médico chegou ao fim, mas a conta não foi ativada. Quase sempre é cartão de uma conexão antiga: veja "Se der erro" abaixo.',
        sem_convite: 'Gere o link primeiro e mande para o médico.',
      },
      guia: {
        antesItens: [
          'Número no app WhatsApp Business, atualizado, com o celular por perto. No WhatsApp comum a Meta recusa.',
          'Facebook do médico funcionando. É o login que mais trava.',
          'Número nunca conectado antes a outra plataforma com cartão. Se já foi, leia "Se der erro" antes de mandar o link.',
        ],
        passos: [
          {
            titulo: 'Abre o link',
            texto:
              'Vê um passo a passo de 6 telas e toca em "Conectar meu WhatsApp". Vai para a Gupshup, a parceira da Meta que paga as mensagens pela Zenda.',
          },
          {
            titulo: 'Entra com o Facebook',
            texto: 'Toca em "Continue with Facebook" e, na janela da Meta, em "Continuar".',
          },
          {
            titulo: 'Digita o número do consultório',
            texto:
              'Em "Adicione seu número de telefone", deixa "Insira um novo número" e digita o número do WhatsApp Business. Confere nome e foto e avança. O portfólio já vem escolhido.',
          },
          {
            titulo: 'Lê o QR code',
            texto:
              'No WhatsApp Business, toca em "Conectar" na mensagem do Facebook Business (ou Configurações › Conta › Plataforma Business) e aceita compartilhar o histórico.',
          },
          {
            titulo: 'Pronto',
            texto:
              'A Gupshup mostra "Congratulations". Em segundos o número aparece conectado aqui. Se não aparecer, toque em "Conferir se o médico terminou".',
          },
        ],
        alerta:
          'Não há passo de cartão. Se a Meta oferecer "Adicionar forma de pagamento", pule: quem paga é a Zenda.',
        depois:
          'Depois de conectar, o médico continua usando o WhatsApp Business no celular, como sempre.',
      },
      erros: {
        titulo: 'Se der erro',
        itens: [
          {
            titulo: '"Unable to add credit line to WABA"',
            texto:
              'O número já tinha cartão de uma conexão antiga com a Meta, e a Meta não troca o cartão dessa conta. Saída: o médico faz backup das conversas, apaga e recria a conta no WhatsApp Business, restaura o backup e refaz a conexão. Atenção: conta recriada cai no erro abaixo por alguns dias.',
          },
          {
            titulo: '"Seu número não está qualificado"',
            texto:
              'A Meta exige uso real do WhatsApp Business antes de conectar, e conta nova não tem. Não dá para acelerar: o médico usa o app normalmente por alguns dias e tenta de novo. Recriar a conta de novo zera a contagem.',
          },
          {
            titulo: 'Outro erro',
            texto:
              'Peça um print. A mensagem da Meta traz um código entre parênteses, por exemplo (#3441045), e é ele que diz o que aconteceu.',
          },
        ],
      },
      custosNota:
        'Valores por mensagem entregue no Brasil, tabela da Meta de 01/10/2026. Quem paga é a Zenda, pela Gupshup.',
      checklist: {
        login: 'Ele consegue entrar no Facebook agora? É o passo que mais trava.',
        phone: 'O número está no WhatsApp Business atualizado, e o celular está com ele?',
        identity: 'O nome que o paciente vai ver já está decidido?',
        historico:
          'O número nunca foi conectado a outra plataforma com cartão dele? Se já foi, veja "Se der erro" antes.',
      },
      inviteAbertura: (primeiro: string) =>
        `Oi, ${primeiro}! Para eu responder seus pacientes pelo WhatsApp do consultório, preciso que você conecte o número. Leva uns 5 minutos, e o link explica cada passo.`,
      inviteFecho: (atendente: string) =>
        `Te mando o link em seguida. Se preferir, fazemos juntos por telefone.

${atendente}`,
      invite: [
        'Antes, confere 2 coisas:',
        '',
        '1) Você consegue entrar no seu Facebook agora? Se a senha sumiu, recupera antes.',
        '2) O número está no app WhatsApp Business, atualizado, com o celular por perto? No WhatsApp comum não funciona.',
        '',
        'Você não cadastra cartão: o custo das mensagens fica com a Zenda. O número e a conta continuam seus, e você desconecta quando quiser.',
        '',
        'Se esse número já foi conectado antes a alguma plataforma com cartão seu, me avisa antes: tem um passo a mais.',
      ],
      carrossel: {
        passoDe: (i: number, n: number) => `${i} de ${n}`,
        navegacao: 'Telas do passo a passo',
        anterior: 'Voltar',
        proximo: 'Avançar',
        pular: 'Já sei, conectar agora',
        conectar: 'Conectar meu WhatsApp',
        indo: 'Abrindo a conexão…',
        rodape:
          'Você segue para a Gupshup e entra com o seu Facebook. A Zenda não vê nem guarda a sua senha.',
        telas: (operacao: string) => [
          {
            titulo: 'O que vai acontecer',
            texto: `${operacao} vai responder seus pacientes pelo WhatsApp do consultório. Você continua usando o WhatsApp Business no celular, com as mesmas conversas.`,
            itens: [
              'Leva uns 5 minutos.',
              'Deixe o celular do consultório por perto.',
              'Você vai entrar com o seu Facebook.',
            ],
          },
          {
            titulo: 'Quem é a Gupshup',
            texto:
              'A conexão passa pela Gupshup, parceira oficial da Meta, a dona do WhatsApp. É por ela que a Zenda paga as mensagens: você não cadastra cartão.',
            itens: [
              'O número e a conta continuam seus.',
              'Você desconecta quando quiser, no próprio WhatsApp Business.',
            ],
          },
          {
            titulo: 'Passo 1: entre com o Facebook',
            texto:
              'Na página da Gupshup, toque em "Continue with Facebook" e entre com a conta que administra o consultório. Na janela da Meta, toque em "Continuar".',
          },
          {
            titulo: 'Passo 2: digite o número',
            texto:
              'Em "Adicione seu número de telefone do WhatsApp", deixe "Insira um novo número de telefone" e digite o número do consultório, o mesmo do WhatsApp Business.',
            itens: [
              'A Meta mostra o nome e a foto do perfil. Confira e toque em "Avançar".',
              'O portfólio já vem escolhido. Toque em "Avançar" de novo.',
            ],
          },
          {
            titulo: 'Passo 3: leia o QR code',
            texto:
              'No celular, abra o WhatsApp Business e toque em "Conectar" na mensagem do Facebook Business. Depois leia o QR code que aparece na tela.',
            itens: [
              'Não achou a mensagem? Configurações › Conta › Plataforma Business.',
              'Aceite compartilhar o histórico: é assim que as conversas aparecem para a atendente.',
              'Quando a Gupshup mostrar "Congratulations", pronto.',
            ],
          },
          {
            titulo: 'Se aparecer um erro',
            texto: `Não tente várias vezes. Tire um print e mande para ${operacao}.`,
            itens: [
              '"Unable to add credit line to WABA": o número já tem cartão de uma conexão antiga com a Meta. Tem um passo a mais, e a atendente explica.',
              '"Seu número não está qualificado": a Meta pede mais uso do WhatsApp Business antes de conectar. Use normalmente por alguns dias.',
            ],
          },
        ],
      },
    },
    guia: {
      antes: 'Antes de começar',
      antesItens: [
        'O número precisa estar no app WhatsApp Business, atualizado, no celular do consultório. No WhatsApp comum a Meta recusa o número e diz que ele já está registrado.',
        'Entre no Facebook com a conta que administra o consultório.',
        'Tenha em mãos um cartão de crédito ou débito válido e o CPF ou CNPJ do consultório.',
      ],
      passos: [
        {
          titulo: 'Abra o link e toque em "Conectar meu WhatsApp"',
          texto:
            'Abre uma janela da Meta. Na tela "Conecte sua conta facilmente a Zenda", toque em Continuar.',
        },
        {
          titulo: 'Conecte o WhatsApp Business que já existe',
          texto:
            'Escolha a opção de conta existente e digite o número do consultório. A Meta mostra o nome e a foto do perfil: confira e toque em Avançar.',
        },
        {
          titulo: 'Escolha o portfólio empresarial',
          texto:
            'Use o portfólio do consultório, ou crie um. Se a Meta disser que o limite de portfólios foi atingido, escolha um que já existe.',
        },
        {
          titulo: 'Leia o QR code com o WhatsApp Business',
          texto:
            'No celular, abra o WhatsApp Business, procure a mensagem da conta oficial do Facebook Business e toque em Conectar. Se ela não aparecer, abra o menu ou as configurações do app e leia o QR code da tela. Depois escolha se compartilha o histórico dos últimos 6 meses.',
        },
        {
          titulo: 'Confirme o nome da conta e o fuso horário',
          texto:
            'Deixe o fuso em (GMT-03:00) America/Sao_Paulo e toque em Avançar. Espere a tela "Conectando sua conta" terminar.',
        },
      ],
      passoCartaoMedico: {
        titulo: 'Cadastre o cartão agora. Não pule este passo',
        texto:
          'Na tela "Sua conta está conectada", toque em "Adicionar forma de pagamento", e não em Concluir. Escolha cartão de crédito ou débito e preencha. Em seguida informe o CPF ou CNPJ, marque que a compra é para fins comerciais e toque em Salvar.',
      },
      passoCartaoAtendente: {
        titulo: 'Adicione a atendente para ela cadastrar o cartão',
        texto:
          'Na tela "Sua conta está conectada", toque em Concluir. Depois adicione a atendente ao seu portfólio empresarial com permissão financeira: é ela quem cadastra o cartão.',
      },
      alertaCartao:
        'Sem um cartão válido na conta do WhatsApp, o número conecta e não envia nenhuma mensagem.',
      depois:
        'A Meta pode mandar um e-mail dizendo que vai verificar a empresa. Isso não trava a conexão: o número já aparece conectado na Zenda.',
    },
    custos: {
      janelaTitulo: 'A janela de 24 horas',
      janela:
        'Cada mensagem do paciente abre 24 horas em que a atendente responde à vontade. Exemplo: ele escreve às 10h de segunda; até 10h de terça, ela responde normalmente.',
      foraTitulo: 'Depois que a janela fecha',
      fora: 'Para falar de novo, só com um template aprovado pela Meta. Desde 01/10/2026 a Meta cobra toda mensagem entregue, dentro ou fora da janela.',
      colunaTipo: 'Mensagem entregue',
      colunaValor: 'Valor',
      servico: 'Resposta dentro da janela de 24 horas',
      servicoGratisAte: 'grátis até 30/09/2026',
      utilidade: 'Modelo de utilidade: lembrete, confirmação, aviso de consulta',
      marketing: 'Modelo de marketing: promoção, campanha, novidade',
      nota: (cartaoDaAtendente: boolean) =>
        `Valores por mensagem entregue no Brasil, pela tabela oficial da Meta válida a partir de 01/10/2026. A cobrança sai do cartão ${cartaoDaAtendente ? 'da atendente, cadastrado' : 'cadastrado'} na conta do WhatsApp do número, em reais.`,
      fonte: 'Tabela oficial de preços da Meta',
    },
    onboarding: {
      checklist: {
        login:
          'Ele consegue entrar na conta do Facebook/Meta AGORA? (é o passo 1, e o que mais trava)',
        phone:
          'O número está no app WhatsApp Business, atualizado, e o celular está com ele. No WhatsApp comum a Meta recusa o número.',
        identity: 'CNPJ e o nome que deve aparecer para o paciente já decididos.',
        cardYours: 'Você tem SEU cartão em mãos: o cartão é seu, não dele.',
        adminAtEnd:
          'Ele sabe que vai te adicionar como administradora ao final. Sem isso você não consegue anexar o cartão.',
        cardTheirs:
          'Cartão de crédito ou débito válido e CPF ou CNPJ em mãos: a cobrança da Meta é na conta dele, não na sua. Sem cartão o número conecta e não envia.',
      },
      invite: {
        opening: (primeiro: string) =>
          `Oi, ${primeiro}! Para eu conseguir cuidar dos seus pacientes pelo WhatsApp, a Meta (dona do WhatsApp) pede um cadastro rápido. Leva uns 10 minutos e a gente faz junto, por telefone.`,
        attendant: [
          'Antes da gente marcar, confere 2 coisas:',
          '',
          '1) Você consegue entrar na sua conta do Facebook agora? É o primeiro passo do cadastro e o que mais trava. Se a senha sumiu, recupera antes.',
          '2) O número no app WhatsApp Business, atualizado, com o celular por perto. No WhatsApp comum não funciona.',
          '',
          'Você NÃO precisa cadastrar cartão nenhum. No final, você só vai me adicionar como administradora da sua conta, e eu cuido da parte de pagamento da Meta, já incluída no que você me paga.',
          '',
          'Sobre esse acesso: a conta continua sendo sua, no seu CNPJ, e o número também. Se um dia a gente parar de trabalhar junto, você me remove e fica com tudo. Eu preciso do acesso para poder anexar meu cartão e resolver as pendências da Meta sem te incomodar toda vez.',
        ],
        client: (url: string) => [
          'Antes da gente marcar, confere 3 coisas:',
          '',
          '1) Você consegue entrar na sua conta do Facebook agora? É o primeiro passo do cadastro e o que mais trava. Se a senha sumiu, recupera antes.',
          '2) Um cartão de crédito ou débito válido e o CPF ou CNPJ do consultório.',
          '3) O número no app WhatsApp Business, atualizado, com o celular por perto. No WhatsApp comum não funciona.',
          '',
          'Sobre o cartão: a conta é SUA, no seu CNPJ, e você vê quanto gastou quando quiser. A Meta cobra as mensagens direto de você (costuma ser pouco), e desde julho a cobrança vem em reais, da Facebook Brasil. Eu não tenho acesso ao seu cartão e não cobro nada por mensagem: a regra da Meta é que a conta fique no nome de quem é dono do número, e o número continua sendo seu.',
          '',
          'Quanto custa: a partir de 01/10/2026 a Meta cobra por mensagem entregue. Resposta dentro de 24 horas depois da mensagem do paciente: R$ 0,035. Lembrete ou confirmação fora dessa janela: R$ 0,035. Mensagem de promoção: R$ 0,32.',
          '',
          `Passo a passo oficial da Meta para cadastrar o cartão: ${url}`,
        ],
        closing: (atendente: string) => `Me diz um horário que a gente faz junto.\n\n${atendente}`,
      },
    },
  },

  /* ── Console de verificação ───────────────────────────────────────────── */
  verification: {
    title: 'Console de verificação',
    subtitle:
      'As duas chamadas que a Meta exige para aprovar o app, uma por permissão. Esta tela existe para ser gravada, e sai do ar quando a aprovação sair.',
    ready: 'Número de teste pronto',
    notReady: 'Falta configurar',
    missingEnv:
      'Faltam as credenciais do número de teste no .env. Elas estão em Casos de uso → Conectar no WhatsApp → Etapa 1.',
    testNumber: 'Número de teste',
    account: 'Conta (WABA)',
    graphVersion: 'Graph API',
    sendTitle: 'Enviar mensagem',
    sendSub:
      'Grave este bloco sozinho. A Meta quer ver a mensagem saindo daqui e chegando no WhatsApp: filme a tela e o aparelho.',
    to: 'Para (com DDI)',
    toHint:
      'Precisa estar na lista de destinatários verificados do número de teste: até 5, cadastrados no painel da Meta.',
    type: 'Tipo',
    typeTemplate: 'Template',
    typeText: 'Texto livre',
    typeHintTemplate: 'Funciona sempre. É o único jeito de iniciar uma conversa.',
    typeHintText:
      'Só funciona com a janela de 24 h aberta: o destinatário precisa ter escrito antes.',
    message: 'Mensagem',
    templateApproved: 'Template aprovado',
    templateHint:
      'hello_world já existe em toda conta de teste, não precisa criar nada para o primeiro envio.',
    sendButton: 'Enviar pelo WhatsApp',
    sending: 'Enviando…',
    sendOk: 'Mensagem aceita pela Meta',
    sendTo: (numero: string, o: string) => `para ${numero} · ${o}`,
    templateTitle: 'Criar template',
    templateSub:
      'Grave este bloco em vídeo separado. Mostrar as duas permissões no mesmo vídeo é causa documentada de rejeição.',
    templateName: 'Nome',
    templateNameHint: 'Minúsculas, números e underscore. O nome é corrigido automaticamente.',
    templateBody: 'Corpo',
    templateBodyHint:
      'Sem especialidade, procedimento ou diagnóstico. O template passa pela Meta e é lido por revisor humano: informação clínica aqui é vazamento de dado de saúde, e o envio é bloqueado antes de sair.',
    createButton: 'Criar template',
    creating: 'Criando…',
    templateOk: 'Template criado na conta da Meta',
    sampleText:
      'Oi! Aqui é a secretária do consultório. Recebi sua mensagem e já estou verificando.',
    sampleTemplateName: 'zenda_confirmacao_horario',
    sampleTemplateBody:
      'Ola! Confirmando seu horario de amanha as 14h. Responda SIM para confirmar ou NAO para remarcar.',
  },

  /* ── Rótulos de enum ──────────────────────────────────────────────────── */
  enums: {
    waType: {
      image: 'Foto',
      audio: 'Áudio',
      video: 'Vídeo',
      document: 'Documento',
      sticker: 'Figurinha',
      location: 'Localização',
      contacts: 'Contato',
    } as Readonly<Record<string, string | undefined>>,
    clientStatus: { active: 'Ativo', paused: 'Pausado', churned: 'Encerrado' },
    conversationStatus: {
      open: 'Aberta',
      pending: 'Aguardando',
      answered_by_doctor: 'Respondida pelo médico',
      resolved: 'Resolvida',
    },
    /* Selo na consulta: de ONDE ela veio, quando nao nasceu aqui. `zenda` nao
       aparece — marcar o normal em toda linha e ruido. `clinic_system` nao diz
       "Doctoralia" porque a grade nao sabe qual plataforma e, e o cabecalho da
       pagina ja diz. */
    appointmentSource: {
      zenda: 'Zenda',
      google_calendar: 'Google Agenda',
      clinic_system: 'Da plataforma',
      import: 'Importada',
    },
    appointmentStatus: {
      requested: 'Solicitada',
      scheduled: 'Agendada',
      confirmed: 'Confirmada',
      checked_in: 'Check-in feito',
      completed: 'Concluída',
      cancelled: 'Cancelada',
      no_show: 'No-show',
    },
    channelState: {
      draft: 'Rascunho',
      verifying: 'Em verificação',
      active: 'Ativo',
      degraded: 'Degradado',
      inactive_warning: 'Inatividade',
      disconnected: 'Desconectado',
      suspended: 'Suspenso',
      offboarded: 'Encerrado',
    },
    quality: {
      GREEN: 'Qualidade alta',
      YELLOW: 'Qualidade média',
      RED: 'Qualidade baixa',
      UNKNOWN: 'Sem leitura',
    },
    qualityShort: { GREEN: 'Alta', YELLOW: 'Média', RED: 'Baixa', UNKNOWN: 'Sem leitura' },
    billing: {
      ok: 'Cobrança em dia',
      unknown: 'Cobrança não verificada',
      missing_payment_method: 'Sem forma de pagamento',
      blocked: 'Cobrança bloqueada',
    },
    billingShort: {
      ok: 'Em dia',
      unknown: 'Não verificada',
      missing_payment_method: 'Sem pagamento',
      blocked: 'Bloqueada',
    },
    modality: { in_person: 'Presencial', telehealth: 'Teleconsulta' },
    delivery: {
      none: '',
      queued: 'Na fila',
      in_doubt: 'Sem confirmação',
      accepted: 'Aceita',
      held: 'Retida',
      sent: 'Enviada',
      failed: 'Falhou',
      delivered: 'Entregue',
      read: 'Lida',
      played: 'Ouvida',
    },
  },

  /* ── Alertas de cliente e canal ───────────────────────────────────────── */
  alerts: {
    disconnectedTitle: 'Conexão caiu',
    disconnectedText:
      'O número não está mais ligado ao Zenda. Nada entra e nada sai até reconectar.',
    suspendedTitle: 'Número suspenso pela Meta',
    suspendedText: 'A conta foi suspensa. Nenhuma mensagem sai até a revisão na Meta.',
    noCardTitle: 'Sem cartão na conta do médico',
    noCardShort: 'Sem cartão na Meta',
    noCardText:
      'A conta dele na Meta não tem forma de pagamento: o número conecta e NÃO envia. Use a mensagem pronta abaixo para conduzir o cadastro.',
    billingBlockedTitle: 'Cobrança bloqueada na Meta',
    billingBlockedText:
      'A Meta bloqueou a cobrança desta conta. Nenhuma mensagem sai até a regularização.',
    billingUnknownTitle: 'Cobrança não verificada',
    billingUnknownShort: 'Cobrança não lida',
    billingUnknownText:
      'A Meta ainda não confirmou se há cartão válido nesta conta. Toque em "Conferir agora" no cartão do canal para ler de novo.',
    inactiveTitle: 'Médico sem abrir o app',
    inactiveTextNoDays: 'A Meta avisou inatividade neste número. Sem uso, a conexão cai.',
    inactiveText: (dias: number) =>
      `Sem sinal do app há ${dias} ${dias === 1 ? 'dia' : 'dias'}. Sem uso, a conexão cai sozinha.`,
    degradedTitle: 'Canal degradado',
    degradedText: 'A entrega está instável neste número. Vale acompanhar hoje.',
    qualityRedTitle: 'Qualidade baixa',
    qualityRedText:
      'Muitos bloqueios de pacientes. O limite de envio pode cair a qualquer momento.',
    qualityYellowTitle: 'Qualidade em queda',
    qualityYellowText: 'A Meta rebaixou a qualidade deste número. Reduza envios em massa.',
    noWhatsappTitle: 'Sem WhatsApp conectado',
    noWhatsappShort: 'Sem WhatsApp',
    noWhatsappText: 'Este cliente ainda não manda nem recebe nada pelo Zenda.',
    overdueTitle: 'Mensalidade em atraso',
    overdueText: 'O vencimento passou e o pagamento não entrou. Cobre antes que vire mês seguinte.',
  },

  /* ── Retorno, em linguagem de gente ───────────────────────────────────── */
  /* ── Equipes (docs/domain/equipes.md) ─────────────────────────────────── */
  equipe: {
    emailObrigatorio: 'Informe o e-mail da pessoa.',
    horarioInvertido: 'O fim precisa ser depois do início.',
    periodoIncompleto: 'Preencha o início e o fim.',
    suasEquipes: 'Suas equipes',
    suasEquipesSub:
      'Atenda junto com outras atendentes. Ex.: você e a Bruna se cobrem nas férias. Cada médico continua com quem o trouxe.',
    nenhuma: 'Nenhuma equipe ainda',
    nenhumaTexto:
      'Crie uma e convide quem atende com você. Cada pessoa decide quais médicos dela entram.',
    nomeDaEquipe: 'Nome da equipe',
    nomePlaceholder: 'Ex.: Plantão da semana',
    criar: 'Criar equipe',
    resumo: (membros: number, medicos: number) =>
      `${membros} ${membros === 1 ? 'pessoa' : 'pessoas'} · ${medicos} ${medicos === 1 ? 'médico' : 'médicos'}`,
    papel: { owner: 'Líder', admin: 'Admin', member: 'Membro' },
    nivel: { atendimento: 'Atendimento', gestao: 'Gestão' },
    nivelExplica: {
      atendimento: 'Conversas, agenda e ficha do paciente. Ex.: quem cobre o plantão.',
      gestao:
        'Tudo do atendimento, mais disponibilidade, locais e configuração da agenda. Ex.: quem organiza a agenda do médico.',
    },
    comoFunciona: 'Como funciona',
    comoFuncionaTexto:
      'Cada pessoa escolhe quais médicos compartilha. Quem está na equipe vê, responde e marca consultas desses médicos. Número, cartão e cobrança ficam só com quem trouxe o médico.',
    naoEncontrada: 'Equipe não encontrada',
    lideradaPor: (nome: string) => `Liderada por ${nome}`,
    renomear: 'Renomear',
    arquivar: 'Arquivar',
    sair: 'Sair da equipe',
    sairDe: (nome: string) => `Sair de ${nome}?`,
    sairExplica:
      'Você deixa de ver os médicos dos outros. Se ninguém mais da sua operação ficar, os seus médicos saem da equipe também.',
    arquivarEquipe: (nome: string) => `Arquivar ${nome}?`,
    arquivarExplica:
      'Todos deixam de ver os médicos uns dos outros. O histórico de quem fez o quê continua guardado.',
    membros: 'Pessoas',
    membrosSub:
      'Líder: muda papéis, passa a liderança, arquiva a equipe e vê o financeiro. Admin: convida e remove membros. Membro: atende os médicos compartilhados.',
    voce: 'você',
    voceMaiusculo: 'Você',
    quemCriou: 'criou a equipe',
    medicosCompartilhados: (n: number) =>
      n === 0
        ? 'nenhum médico compartilhado'
        : `${n} ${n === 1 ? 'médico compartilhado' : 'médicos compartilhados'}`,
    papelDe: (nome: string) => `Papel de ${nome}`,
    tornarLider: 'Tornar líder',
    transferir: 'Transferir',
    transferirPara: (nome: string) => `Passar a liderança para ${nome}?`,
    transferirExplica: (nome: string) =>
      `${nome} passa a liderar e você vira admin. O financeiro dos médicos de outras operações só aparece para a nova liderança depois que cada dona declarar de novo.`,
    remover: 'Remover',
    removerPessoa: (nome: string) => `Remover ${nome}`,
    removerExplica: (nome: string) =>
      `${nome} deixa de ver os médicos da equipe na hora. Se era a última pessoa da operação de ${nome} na equipe, os médicos dessa operação saem junto.`,
    convidar: 'Convidar',
    emailDeQuem: 'E-mail de quem você quer convidar',
    emailPlaceholder: 'E-mail da pessoa no Zenda',
    papelNoConvite: 'Papel no convite',
    conviteEnviado: (email: string) =>
      `Se ${email} tem conta no Zenda, o convite aparece na próxima vez que essa pessoa entrar.`,
    conviteDe: (quem: string, papel: 'admin' | 'member') =>
      `convite de ${quem}, como ${papel === 'admin' ? 'admin' : 'membro'}`,
    cancelarConvite: 'Cancelar convite',
    faixaConvite: (n: number) =>
      n === 1
        ? 'Você tem um convite para entrar numa equipe'
        : `Você tem ${n} convites para entrar em equipes`,
    convitesParaVoce: 'Convites para você',
    convitesParaVoceSub:
      'Para entrar, você aceita o termo de confidencialidade: vai atender pacientes de médicos de outras pessoas.',
    convidadaPor: (quem: string, papel: 'admin' | 'member') =>
      `${quem} convidou você como ${papel === 'admin' ? 'admin' : 'membro'}`,
    verEAceitar: 'Ver e aceitar',
    recusar: 'Recusar',
    recusarConvite: (nome: string) => `Recusar o convite de ${nome}?`,
    naoReceberMais: 'Não receber mais convites desta equipe',
    entrarEm: (nome: string) => `Entrar em ${nome}`,
    termoSub:
      'Leia o termo de confidencialidade. Você vai atender pacientes de médicos de outras pessoas.',
    liETermo: 'Li e aceito o termo de confidencialidade',
    aceitarEEntrar: 'Aceitar e entrar',
    medicos: 'Médicos',
    medicosSub: 'Os que cada operação compartilhou com a equipe, e em que nível.',
    daSuaOperacao: 'Da sua operação',
    deOutrasOperacoes: 'De outras operações',
    nadaCompartilhado: 'Você ainda não compartilhou nenhum médico com esta equipe.',
    soQuemAdministra: 'Quem compartilha os médicos da sua operação é quem a administra.',
    ninguemCompartilhou: 'Ninguém compartilhou médicos com esta equipe ainda.',
    daPessoa: (nome: string) => `de ${nome}`,
    nivelDe: (nome: string) => `Nível de ${nome}`,
    retirar: 'Retirar',
    retirarMedico: (nome: string) => `Retirar ${nome} da equipe?`,
    retirarExplica: (nome: string) =>
      `A equipe deixa de ver as conversas, a agenda e as fichas de ${nome} na hora. As conversas que estavam com alguém de fora voltam para você.`,
    declararDeNovo: (lider: string) =>
      `Declare de novo com o nome de ${lider} para a liderança ver o financeiro`,
    declararPara: (nome: string) => `Declaração de autorização: ${nome}`,
    confirmar: 'Confirmar',
    compartilharMedicos: 'Compartilhar médicos',
    compartilharCom: (nome: string) => `Compartilhar com ${nome}`,
    compartilharSub:
      'Escolha os médicos e o nível. Número, cartão e cadastro continuam só com você.',
    compartilhar: (n: number) =>
      n === 0 ? 'Compartilhar' : `Compartilhar ${n} ${n === 1 ? 'médico' : 'médicos'}`,
    quaisMedicos: 'Quais médicos',
    todosOsMedicos: 'Todos',
    nenhumMedico: 'Nenhum',
    qualNivel: 'Em que nível',
    avisoFinanceiro: (lider: string) =>
      `${lider}, que lidera a equipe, verá a mensalidade, o vencimento e os pagamentos destes médicos. Os outros membros não veem.`,
    confirmoDeclaracao: 'Confirmo a declaração acima',
    sugestao: (medico: string, equipe: string) =>
      `A equipe ${equipe} pede que todo médico novo seja compartilhado. Compartilhar ${medico}?`,
    politicaTodos: 'Sugerir compartilhar todo médico novo',
    politicaTodosExplica:
      'Ao cadastrar um médico, a pergunta já vem marcada para esta equipe. Ele só entra com a declaração dele.',
    financeiro: 'Financeiro dos médicos da equipe',
    financeiroSub:
      'Só a liderança vê, separado por operação: cada contrato é de quem trouxe o médico, então nada é somado.',
    financeiroVazio: 'Nenhuma operação declarou ainda o seu nome como liderança.',
    turnos: 'Turnos',
    turnosSub:
      'Quem cobre o quê e quando. Turno organiza o atendimento, mas não tira o acesso de ninguém.',
    dePlantaoAgora: 'De plantão agora',
    ninguemDePlantao: 'ninguém',
    diasDaSemana: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    tirarTurno: 'Tirar este turno',
    quem: 'Quem',
    eu: 'Eu',
    dia: 'Dia',
    inicio: 'Início',
    fim: 'Fim',
    adicionarTurno: 'Turno',
    tipo: 'Tipo',
    folga: 'Folga',
    extra: 'Extra',
    marcar: 'Marcar',
    distribuir: 'Distribuir conversa nova entre quem está de plantão',
    distribuirExplica:
      'Conversa nova sem dono vai para quem está de plantão e com menos conversas abertas. Ex.: Ana com 3 e Bruna com 1, vai para a Bruna.',
    atividade: 'Atividade',
    atividadeSub: 'O que se fez pela equipe. Você vê o que é seu e o que fizeram nos seus médicos.',
    atividadeDoClienteSub: 'Quem fez o quê neste médico, e quem de fora abriu cada conversa.',
    semAtividade: 'Nada por aqui ainda',
    semAtividadeTexto: 'As ações aparecem aqui assim que alguém atende.',
    pelaEquipe: 'pela equipe',
    acao: {
      enviou_mensagem: 'enviou uma mensagem',
      enviou_template: 'enviou um template',
      enviou_midia: 'enviou um anexo',
      escreveu_nota: 'escreveu uma nota interna',
      abriu_conversa: 'abriu uma conversa',
      resolveu_conversa: 'resolveu uma conversa',
      reabriu_conversa: 'reabriu uma conversa',
      assumiu_conversa: 'assumiu uma conversa',
      soltou_conversa: 'soltou uma conversa',
      etiquetou_conversa: 'etiquetou uma conversa',
      tirou_etiqueta: 'tirou uma etiqueta',
      marcou_consulta: 'marcou uma consulta',
      desmarcou_consulta: 'desmarcou uma consulta',
      remarcou_consulta: 'remarcou uma consulta',
      confirmou_consulta: 'confirmou uma consulta',
      registrou_presenca: 'registrou a presença numa consulta',
      editou_ficha: 'editou uma ficha',
      cadastrou_paciente: 'cadastrou um paciente',
      cadastrou_dependente: 'cadastrou um dependente',
      editou_dependente: 'editou um dependente',
      mudou_agenda: 'mudou a agenda',
      criou_equipe: 'criou a equipe',
      convidou: 'convidou alguém',
      entrou_na_equipe: 'entrou na equipe',
      saiu_da_equipe: 'saiu da equipe',
      removeu_da_equipe: 'removeu alguém da equipe',
      mudou_papel: 'mudou o papel de alguém',
      transferiu_lideranca: 'transferiu a liderança',
      compartilhou_medico: 'compartilhou um médico',
      revogou_medico: 'retirou um médico',
      declarou_autorizacao: 'declarou a autorização de um médico',
    },
  },
  /* ── Ajuda de cada tela ───────────────────────────────────────────────── */
  /* ── Primeira mensagem e nova conversa ─────────────────────────────── */
  /* ── Marcar consulta ──────────────────────────────────────────────────── */
  /* ── Disponibilidade do médico ─────────────────────────────────────────── */
  /* ── Peças pequenas de interface ──────────────────────────────────────── */
  ui: {
    hoje: 'Hoje',
    limpar: 'Limpar',
    semMensagensProntas: 'Nenhuma mensagem pronta ainda. Crie em Configurações, Mensagens prontas.',
    faltaPreencher: 'falta preencher',
    avisos: 'Avisos',
    fecharAviso: 'Fechar aviso',
    calendario: 'Calendário',
    anterior: 'Anterior',
    proximo: 'Próximo',
    limparData: 'Limpar data',
    abrirCalendario: 'Abrir calendário',
    mensagensProntas: 'Mensagens prontas',
    falhaAoBaixar: 'Não deu para baixar o arquivo. Tente de novo.',
    falhaNoAudio: 'Não deu para abrir o áudio. Tente de novo.',
    falhaNoArquivo: 'Não deu para enviar o arquivo. Tente de novo.',
    templateReabre: 'Passadas as 24 h, só um template aprovado reabre a conversa',
  },

  /* ── Grade de horários ───────────────────────────────────────────────── */
  grade: {
    livres: (min: number) => `${Math.floor(min / 60)} h ${min % 60} min livres`,
    resumoDoDia: (n: number, livresMin: number) =>
      `${n} ${n === 1 ? 'consulta' : 'consultas'} · ${Math.floor(livresMin / 60)} h ${livresMin % 60} min livres`,
    cliqueParaMarcar: (min: number) =>
      `Clique para marcar ${min} min, ou arraste para escolher a duração.`,
    arrasteDica:
      'Arraste para abrir atendimento; arraste sobre o verde para tirar. O nome do dia marca ou limpa a linha.',
    abrirOuLimpar: (dia: string) => `Abrir ou limpar ${dia} inteiro`,
    igualSegunda: 'Igual à segunda',
    igualSegundaDica: 'Repete a segunda de terça a sexta.',
    vinteQuatroDica: 'Mostra as 24 h, para quem atende de madrugada ou de noite.',
    diaInteiroDica: 'Mostra as 24 h do dia, inclusive as horas em que ninguém atende.',
    abertoPorExcecao: 'aberto por exceção',
  },

  /* ── Agenda de plataforma de terceiro ────────────────────────────────── */
  integracao: {
    nativa: 'Nativa (Zenda)',
    conflitos: (n: number) =>
      n === 1 ? 'Um conflito para resolver' : `${n} conflitos para resolver`,
    conflitoAjuda:
      'O paciente reservou na plataforma um horário que já estava ocupado aqui. O horário está mesmo ocupado, então é a plataforma que precisa ser corrigida: ligue para ela e remarque.',
    semNome: 'Paciente sem nome',
    resolvido: 'Resolvido',
    resolvidoDica: 'Some daqui. Use depois de já ter falado com o paciente.',
    nomeSimulado: 'Paciente do Doctoralia',
    simularDica: 'Teste: finge que um paciente reservou do lado de lá.',
    desvincular: 'Desvincular',
    desvincularDica: 'Desativa. As consultas que vieram de lá continuam aqui.',
    reservouLa: 'O paciente reservou lá:',
    data: 'Data',
    reservar: 'Reservar',
  },

  /* ── Conexão direta com a Meta (página do médico) ────────────────────── */
  conectarMeta: {
    indisponivel: 'A conexão com a Meta não está disponível agora.',
    titulo: (medico: string) => `Conectar o WhatsApp de ${medico}`,
    lede: 'cuida da sua agenda e precisa da sua autorização para responder seus pacientes pelo WhatsApp no seu nome.',
    continuaSeu:
      'O número continua sendo seu, e a conta na Meta também. Você pode remover o acesso quando quiser.',
    precisaBusiness:
      'O número precisa estar no app WhatsApp Business, atualizado, com o celular por perto. No WhatsApp comum a Meta recusa o número.',
    cartaoDaOperacao: (operacao: string) =>
      `Ao final, adicione ${operacao} ao seu portfólio com permissão financeira: é quem cadastra o cartão.`,
    cartaoDoMedico:
      'Ao final, toque em "Adicionar forma de pagamento" e cadastre um cartão válido. Sem ele o número conecta e não envia nenhuma mensagem.',
    verPassos: 'Ver passo a passo',
    naoLiberada: (operacao: string) =>
      `A conexão pelo WhatsApp ainda não está liberada por aqui. Avise ${operacao} antes de tentar de novo.`,
    passo: (passo: string) => `Na janela da Meta, passo: ${passo}`,
    aguardando: 'Aguardando a Meta…',
    conectar: 'Conectar meu WhatsApp',
    rodape: 'Você vai entrar com a sua conta do Facebook. A Zenda não vê e não guarda a sua senha.',
    guiaTitulo: 'Passo a passo da conexão',
    guiaSub: 'Deixe esta janela aberta ao lado da janela da Meta e siga na ordem.',
    custos: 'Cartão, janela de 24 horas e quanto a Meta cobra',
  },

  disp: {
    extraSemHorario: 'Para atender extra, diga de que horas a que horas.',
    soUmHorario: 'Preencha o início e o fim, ou deixe os dois vazios.',
    fimAntes: 'O fim precisa ser depois do início.',
    ateAntes: '"Até quando" precisa ser depois da data inicial.',
    desativarLocal: 'Desativar. As regras deste local param junto.',
    teleconsulta: 'Teleconsulta',
    adicionarLocal: 'Adicionar local',
    ate: 'até',
    ateDica: 'Vazio = sem data final',
    validoAte: 'Válido até',
    nomeDoLocal: 'Nome do local',
    cidade: 'Cidade',
    criar: 'Criar',
    excecoesTitulo: 'Exceções em datas específicas',
    excecoesAjuda:
      'A grade acima é a rotina que se repete. Aqui você quebra a rotina numa data só, sem mexer nela: é onde se abre ou se fecha atendimento num dia específico.',
    naoAtende: 'Não atende',
    atendeExtra: 'Atende extra',
    extra: 'Extra',
    naoAtendeDica: 'Some da agenda o que a rotina criaria nesse dia: feriado, férias, congresso.',
    atendeExtraDica:
      'Cria atendimento onde a rotina não tem: um sábado de mutirão, uma tarde esticada, um dia no meio das férias.',
    dataDaExcecao: 'Data da exceção',
    ateQuando: 'Até quando',
    ateQuandoDica: 'Até quando. Vazio = só o dia ao lado',
    vazioDiaInteiro: 'Vazio = dia inteiro',
    obrigatorio: 'Obrigatório',
    motivo: 'Motivo',
    semExcecoes: 'A rotina vale para todos os dias.',
    regrasTitulo: 'Regras de agendamento',
    antecedenciaRotulo: 'Só aceitar com pelo menos',
    antecedenciaDica:
      '…de antecedência. Um horário que começa antes disso não pode mais ser marcado. É o que impede o médico de descobrir a consulta com o paciente já na porta.',
    horizonteRotulo: 'Abrir agenda com até',
    horizonteDica:
      '…de antecedência. Sem esse teto, disponibilidade sem data final deixa marcar para daqui a anos.',
    deslocamentoRotulo: 'Trocar de endereço leva',
    deslocamentoDica: 'Folga exigida entre duas consultas em locais diferentes no mesmo dia.',
    dias: 'dias',
    duracaoInvalida: 'A duração precisa estar entre 5 e 480 minutos.',
    tiposTitulo: 'Tipos de consulta',
    tiposAjuda:
      'O que aparece na hora de marcar, com a duração de cada um. Na marcação a duração continua editável: aqui é o padrão do tipo.',
    tipoPlaceholder: 'Retorno, primeira consulta, encaixe…',
    semTipos: 'Nenhum tipo ainda. Sem tipo, a marcação pede a duração a cada consulta.',
    tipoComumDica:
      'Vale para todos os médicos da sua operação, então não se edita pela ficha de um cliente. Se este médico usa outra duração, crie um tipo próprio.',
    todos: 'todos',
    salvar: 'Salvar',
    cancelar: 'Cancelar',
    editarTipo: 'Mudar o nome ou a duração',
    desativar: 'Desativar',
    desativarTipo: 'Some da marcação. As consultas já marcadas com ele não mudam.',
    diaInteiro: 'dia inteiro',
    remover: 'Remover',
    salvo: 'salvo',
    nomeDoTipo: 'Nome do tipo',
    duracaoMin: 'Duração em minutos',
  },

  marcar: {
    escolhido: (quando: string) => `Escolhido: ${quando}`,
    ver: 'ver',
    periodos: { qualquer: 'Qualquer', manha: 'Manhã', tarde: 'Tarde' },
    titulo: 'Marcar consulta',
    periodo: 'Período',
    fechar: 'Fechar',
    diasQuePode: 'Dias que o paciente pode',
    dicaTipo: 'Escolher um tipo preenche a duração ao lado.',
    dicaTipoHorario:
      'Escolher um tipo ajusta a duração. Mudar a duração para um valor que não é de nenhum tipo deixa este campo vazio.',
    semTipo: 'Sem tipo',
    dicaDuracao: 'Define quais horários cabem, e por isso muda a lista.',
    dura: 'Dura',
    min: 'min',
    diaAnterior: 'Dia anterior com vaga',
    escolherDia: 'Escolher um dia no calendário',
    proximoDia: 'Próximo dia com vaga',
    procurando: 'Procurando…',
    nenhumHorario: 'Nenhum horário livre com esses filtros.',
    afrouxe: 'Tente afrouxar os dias, ou use encaixe.',
    assunto: 'Assunto (opcional), ex.: Botox, retorno',
    assuntoDica: 'Anotação interna: o paciente nunca vê.',
    repetir: 'Repetir',
    vezesACada: (n: number) => `${n === 1 ? 'vez' : 'vezes'}, a cada`,
    semanas: (n: number): string => (n === 1 ? 'semana' : 'semanas'),
    conferindo: 'Conferindo as datas…',
    conflitos: (n: number) =>
      n === 1
        ? '1 data está ocupada e será pulada. Ligue o encaixe para marcar mesmo assim.'
        : `${n} datas estão ocupadas e serão puladas. Ligue o encaixe para marcar mesmo assim.`,
    conflitosNoIntervalo: (n: number) =>
      n === 1
        ? '1 data já tem consulta nesse intervalo e será pulada. Ligue o encaixe para marcar mesmo assim.'
        : `${n} datas já têm consulta nesse intervalo e serão puladas. Ligue o encaixe para marcar mesmo assim.`,
    dicaEncaixe:
      'Mostra também os horários cheios. Encaixar soma mais uma pessoa no bloco. Não desmarca quem já estava.',
    dicaEncaixeHorario:
      'Aceita duas consultas no mesmo intervalo. Não desmarca quem já estava, e não é necessário para marcar fora do horário de rotina.',
    encaixe: 'Encaixe',
    marcando: 'Marcando…',
    marcar: 'Marcar',
    marcarN: (n: number) => `Marcar ${n}`,
    foraDaJanela: 'Fora do horário em que esse médico atende nesse dia. Pode marcar.',
    estouraJanela: 'Essa consulta passa do fim do atendimento. Pode marcar.',
    buscarPaciente: 'Buscar paciente por nome ou telefone',
    nenhumPaciente: 'Nenhum paciente deste cliente com esse nome.',
    trocar: 'trocar',
  },

  reabrir: {
    criarTemplate: 'Criar um template',
    carregando: 'Carregando os templates…',
    semAprovadoPrimeira:
      'Este médico ainda não tem template aprovado pela Meta, e a primeira mensagem para o paciente só pode ser um template.',
    semAprovadoJanela:
      'Este médico ainda não tem template aprovado pela Meta. Sem um, não dá para mandar a primeira mensagem nem escrever depois das 24 h.',
    escolha: 'Escolha um template aprovado…',
    dicaComeca: 'Começa a conversa: o paciente recebe e pode responder por aqui',
    dicaReabre: 'Reabre a conversa: o paciente recebe e a janela de 24 h volta a contar',
    enviando: 'Enviando…',
    enviar: 'Enviar template',
  },

  novaConversa: {
    comecar: 'Começar conversa',
    titulo: (nome: string) => `Mensagem para ${nome}`,
    subtitulo: 'A primeira mensagem vai por template aprovado.',
    botao: 'Mensagem',
    qualMedicoHint:
      'Escolha por qual médico escrever: a mensagem sai do WhatsApp dele, e a conversa fica com ele.',
    abrirConversa: 'Abrir a conversa',
    precisaAutorizacao: 'Falta registrar que o paciente autorizou',
    porQue:
      'A Meta só deixa a empresa escrever primeiro para quem autorizou receber mensagens. Diga como ele autorizou e siga para o template.',
    registrarEContinuar: 'Registrar e continuar',
    contatos: 'Contatos sem conversa',
    contatoDe: (medicos: string) => `Contato · ${medicos}`,
  },

  piloto: {
    titulo: 'Piloto automático',
    modos: { off: 'Desligado', copiloto: 'Copiloto', piloto: 'Piloto' },
    modosCurtos: { off: 'Off', copiloto: 'Copiloto', piloto: 'Piloto' },
    modosLongos: {
      off: 'Nada muda: você responde tudo.',
      copiloto: 'A IA prepara a resposta no campo. Nada sai sem você.',
      piloto: 'A IA responde sozinha o que sabe. O resto fica com você.',
    },
    herdar: 'Igual à operação',
    precisam: (n: number) => (n === 1 ? '1 precisa de você' : `${n} precisam de você`),
    nenhumPrecisa: 'Nada precisa de você',
    pausas: {
      orcamento: 'pausado: orçamento do mês',
      kill: 'pausado pela plataforma',
      ia_indisponivel: 'pausado: IA indisponível',
    },
    estados: {
      responding: 'Piloto',
      needs_human: 'Precisa de você',
      taken_over: 'Assumida',
    },
    novas: (n: number) => `+${n} ${n === 1 ? 'nova' : 'novas'}`,
    respondeEm: (t: string) => `piloto responde em ${t}`,
    respondoEu: 'Respondo eu',
    aguarda: (hora: string) => `piloto responde às ${hora} se ninguém responder`,
    aguardaDica:
      'Alguém respondeu nesta conversa há pouco, e o piloto não fala por cima de quem está atendendo. O prazo fica em Horários e ritmo.',
    lendo: 'piloto lendo a conversa',
    preparando: 'preparando a sugestão',
    tirar: 'Tirar do piloto',
    tirarDica: 'A IA sai desta conversa até você devolver.',
    devolver: 'Devolver ao piloto',
    selo: 'piloto',
    corrigir: 'Corrigir',
    corrigirDica: 'Abre o texto do piloto no campo. A sua versão vira proposta de memória.',
    corrigindo: 'Corrigindo a resposta do piloto: ao enviar, ela vira proposta de memória.',
    cancelar: 'Cancelar',
    devolverBloqueado: 'Paciente sem piloto: tire a marca na ficha para devolver.',
    atrasada: (prazo: string) => `Passou do prazo: ${prazo} no seu horário de trabalho`,
    seloDica: 'Enviada pelo piloto automático. Só você vê este selo.',
    sugestao: 'sugestão do piloto',
    descartar: 'Descartar',
    semPilotoNaConversa: 'Paciente sem piloto: a IA não entra nas conversas dele.',
    pendente: (assunto: string) => `Você ficou de retornar: ${assunto}.`,
    pendenteExplica:
      'O piloto não toca nesse assunto nesta conversa até você liberar (ou por 24 h).',
    pendenteLiberar: 'Já resolvi',
    aprender: {
      curto: 'Lembrar para a próxima?',
      editar: 'Editar',
      titulo: (medico: string) =>
        `Adicionar à memória ${/^dra\.?\s/i.test(medico) ? 'da' : /^dr\.?\s/i.test(medico) ? 'do' : 'de'} ${medico}?`,
      texto: 'Da próxima vez que perguntarem isso, o piloto responde assim.',
      pergunta: 'Pergunta',
      resposta: 'Resposta',
      adicionar: 'Adicionar',
      agoraNao: 'Agora não',
      feito: 'Adicionado à memória.',
    },
    hoje: {
      titulo: 'Piloto hoje',
      respondidas: 'respondidas',
      precisam: 'precisaram de você',
      assumidas: 'assumidas',
      motivo: (m: string) => `Motivo mais comum: ${m}`,
      semMotivo: 'Nenhuma escalação hoje.',
      desligado: 'O piloto está desligado. Ligue na barra lateral.',
      orcamento: (usado: string, limite: string, pct: number) =>
        `Orçamento do mês: ${usado} de ${limite} (${pct}%).`,
      orcamentoEstourado: 'Orçamento do mês atingido: o piloto virou copiloto até o mês virar.',
    },
    aba: {
      nav: {
        resumo: 'Resumo',
        memoria: 'O que ele sabe',
        estilo: 'Como ele escreve',
        horarios: 'Quando responde',
        testar: 'Testar',
        aprendizados: 'Aprendizados',
        historico: 'Histórico',
      },
      navTexto: {
        resumo: 'Como o piloto atende este médico, e se ele está pronto.',
        memoria: 'O piloto responde só com o que está aqui, e nunca inventa.',
        estilo: 'Para o paciente sentir que é você de sempre.',
        horarios: 'Em que horas ele responde, quanto espera e as frases fixas.',
        testar: 'Converse como paciente ou rode as perguntas de teste. Nada vai para o WhatsApp.',
        aprendizados: 'Propostas feitas das suas respostas. Nada entra sem o seu clique.',
        historico: 'Cada vez que a memória é salva vira uma versão. Dá para voltar.',
      },
      pronto: 'Pronto para ligar?',
      checkIa: 'IA configurada neste ambiente',
      checkMemoria: (assuntos: number, perguntas: number) =>
        `Memória com ${assuntos} ${assuntos === 1 ? 'informação' : 'informações'} e ${perguntas} ${perguntas === 1 ? 'pergunta' : 'perguntas'}`,
      checkTestes: (ok: number, total: number) => `Testes: ${ok} de ${total} passaram`,
      checkTestesNenhum: 'Nenhuma pergunta de teste ainda',
      checkAprendizados: (n: number) =>
        n === 0
          ? 'Nenhum aprendizado esperando'
          : `${n} ${n === 1 ? 'aprendizado esperando' : 'aprendizados esperando'} você`,
      ir: 'Abrir',
      informacoes: 'Informações',
      informacoesTexto: 'Convênios, valores, endereço, estacionamento, política de atraso.',
      colunaAssunto: 'Assunto',
      colunaResposta: 'O que dizer',
      pacienteVe: 'O paciente pode saber',
      pacienteNaoVe: 'Só a IA sabe: nunca diz ao paciente',
      perguntasTexto: 'O jeito exato de responder as perguntas mais comuns.',
      regrasTexto2: 'O que o piloto nunca faz. Ex.: "nunca informe o valor da cirurgia".',
      naoSalvo: 'Alterações não salvas',
      descartarAlteracoes: 'Descartar',
      vazio: 'Nada aqui ainda.',
      ritmoTitulo: 'Ritmo',
      ritmoTexto:
        'Quanto tempo depois da última mensagem do paciente ele responde, para parecer gente.',
      limitesTitulo: 'Limites',
      limitesTexto: 'Quando ele para e chama você.',
      posicao: 'Posição deste médico',
      posicaoTexto: 'Vence o controle geral da barra lateral.',
      vale: (m: string) => `Valendo agora: ${m}`,
      iaPronta: (modelo: string) => `IA pronta (${modelo}).`,
      iaFora: 'A IA não está configurada neste ambiente: tudo escala para você.',
      memoria: 'Memória',
      memoriaTexto:
        'O que o piloto sabe deste consultório. Ele responde só com isto, e nunca inventa.',
      memoriaTamanho: (usado: number, teto: number) => `${usado} de ${teto} tokens`,
      memoriaCheia:
        'A memória passou do teto: o piloto usa só as entradas mais parecidas com a pergunta.',
      sobre: 'Sobre o consultório',
      sobreTexto:
        'Convênios, valores, estacionamento, política de atraso. Só sai o que estiver marcado "pode informar".',
      rotulo: 'Assunto',
      textoDoCampo: 'O que dizer',
      podeInformar: 'pode informar',
      novoCampo: 'Adicionar assunto',
      perguntas: 'Perguntas e respostas',
      novaPergunta: 'Adicionar pergunta',
      importar: (n: number) => `Importar respostas prontas (${n})`,
      aprendidoEm: (d: string) => `aprendido em ${d}`,
      regras: 'Regras',
      regrasTexto: 'Ex.: "nunca informe o valor da cirurgia, sempre me chame".',
      novaRegra: 'Adicionar regra',
      estilo: 'Estilo',
      estiloTexto: 'Como você escreve para os pacientes deste médico.',
      tratamento: 'Tratamento',
      voce: 'você',
      senhor: 'senhor / senhora',
      emoji: 'Usa emoji',
      assinatura: 'Assinatura',
      formalidade: 'Tom',
      formalidades: { informal: 'informal', neutra: 'neutro', formal: 'formal' },
      frases: 'Frases que você sempre usa (uma por linha)',
      observacoes: 'Observações',
      rascunhoDeEstilo: 'Sugerir a partir das minhas mensagens',
      exemplos: 'Exemplos (até 10)',
      exemplosTexto: '"Paciente pergunta, eu respondo assim."',
      paciente: 'Paciente',
      eu: 'Eu',
      novoExemplo: 'Adicionar exemplo',
      salvar: 'Salvar memória',
      salvo: (v: number) => `Versão ${v} salva`,
      versoes: 'Versões',
      versao: (v: number, quando: string, quem: string | null) =>
        `Versão ${v} · ${quando}${quem === null ? '' : ` · ${quem}`}`,
      restaurar: 'Voltar a esta',
      aprendizados: 'Aprendizados',
      aprendizadosTexto: 'Propostas feitas das suas respostas. Nada entra sem o seu clique.',
      semAprendizados: 'Nenhuma proposta agora.',
      aprovar: 'Aprovar',
      horarios: 'Horários e ritmo',
      respondeQuando: 'Quando a IA responde',
      sempre: 'Sempre',
      soNestes: 'Só nestes horários',
      limiar: 'Confiança mínima',
      antecedencia: 'Antecedência mínima para oferecer horário (min)',
      seguidas: 'Respostas seguidas antes de chamar você',
      humanoAtivo: 'Silêncio depois que você ou o médico respondem (min)',
      esperaMin: 'Espera mínima (s)',
      esperaMax: 'Espera máxima (s)',
      salvarConfig: 'Salvar',
      frasesTitulo: 'Frases fixas',
      emergencia: 'Mensagem de emergência',
      emergenciaTexto:
        'A única resposta de saúde. Sai uma vez por dia, em qualquer estado da conversa.',
      reservaDentro: 'Ao escolher horário, dentro do seu expediente',
      reservaFora: 'Ao escolher horário, fora do seu expediente',
      simulador: 'Simulador',
      simuladorTexto:
        'Converse como se fosse paciente. Nada vai para o WhatsApp. Tente enganar a IA e veja que ela para.',
      escreva: 'Escreva como paciente…',
      enviar: 'Enviar',
      limpar: 'Recomeçar',
      acoes: {
        nada: 'ficaria em silêncio',
        responder: 'responderia',
        reservar: 'mandaria a frase de reserva e chamaria você',
        escalar: 'chamaria você',
        emergencia: 'mandaria a mensagem de emergência e chamaria você',
      },
      porque: (m: string) => `Motivo: ${m}`,
      portao: (n: number) => `parou no portão ${n}`,
      fontes: 'Fontes',
      testes: 'Conjunto de teste',
      testesTexto: 'As perguntas mais comuns deste consultório. Rode antes de ligar o piloto.',
      pergunta: 'Pergunta do paciente',
      esperado: 'A resposta deve conter',
      esperaAcao: 'Esperado',
      deveResponder: 'responder',
      deveEscalar: 'chamar você',
      adicionarTeste: 'Adicionar',
      rodar: 'Rodar testes',
      passou: 'passou',
      falhou: 'falhou',
      mes: 'Este mês',
      respondidasMes: 'respondidas',
      escaladasMes: 'escaladas',
      custoMes: 'custo do modelo',
      motivosMes: 'O que mais faltou na memória',
      sombra: 'Acordo do copiloto (7 dias)',
      sombraTexto: (a: number, c: number) =>
        c === 0 ? 'Ainda sem sugestões usadas.' : `${a} de ${c} sugestões enviadas sem mudar nada`,
    },
    config: {
      titulo: 'Piloto automático',
      expediente: 'Seu horário de trabalho',
      expedienteTexto: 'Conta o prazo do "Precisa de você" e escolhe a frase de reserva.',
      prazo: 'Prazo até ficar vermelho (min)',
      email: 'E-mail para aviso com a aba fechada',
      emailTexto: 'Só contagens por médico, nunca nome de paciente.',
      emailDepois: 'Avisar depois de (min)',
      orcamento: 'Orçamento do mês (R$)',
      orcamentoTexto: 'Em 100% o piloto vira copiloto sozinho.',
      som: 'Som no aviso',
      resumo: 'Resumo do dia por e-mail',
      salvar: 'Salvar',
      salvo: 'Salvo',
    },
    dias: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
    ficha: {
      titulo: 'Piloto',
      sem: 'Sem piloto',
      semTexto: (desde: string, motivo: string | null) =>
        `Desde ${desde}${motivo === null ? '' : ` (${motivo})`}. A IA não entra nas conversas deste paciente.`,
      com: 'O piloto pode responder este paciente.',
      marcar: 'Marcar sem piloto',
      tirar: 'Tirar a marca',
    },
    notificacao: (medico: string) => `Uma conversa de ${medico} precisa de você`,
  },
  ajuda: {
    botao: 'Como funciona',
    entendi: 'Entendi',
    telas: {
      painel: {
        titulo: 'Como funciona o Painel',
        resumo: 'O resumo do que precisa de você agora, de todos os médicos.',
        secoes: [
          {
            titulo: 'Os números do topo',
            itens: [
              'Clientes com pendência: médico com conexão caída, qualidade em queda ou outro problema no WhatsApp. Clique para ver qual.',
              'Janelas fechando: conversas em que o paciente escreveu há quase 24 h. Responda antes, porque depois só com template.',
              'Conversas aguardando: o paciente falou e ninguém respondeu.',
              'Cobranças em atraso: mensalidade de cliente vencida e não marcada como recebida.',
              'Receita mensal: a soma das mensalidades dos clientes ativos.',
            ],
          },
          {
            titulo: 'As listas',
            itens: [
              'Conversas aguardando resposta: ordenadas pela janela que fecha primeiro. O selo à direita diz quem assumiu. Clique para abrir a conversa.',
              'Agenda de hoje: as consultas do dia de todos os médicos, com o estado de cada uma.',
            ],
          },
        ],
        dica: 'Comece o dia por aqui: o que está em vermelho é o que vira reclamação do médico se esperar.',
      },
      conversas: {
        titulo: 'Como funcionam as Conversas',
        resumo:
          'Todos os pacientes de todos os médicos, numa lista só. À esquerda você escolhe a conversa; no meio, responde.',
        secoes: [
          {
            titulo: 'A lista',
            itens: [
              'Busca: pelo nome, pelo telefone (com ou sem máscara) ou pelo médico. Pacientes sem conversa aparecem embaixo, em "Contatos sem conversa": clique para mandar a primeira mensagem por template.',
              'Filtros: Todas, Aguardando (falta responder), Resolvidas, Minhas (as que você assumiu) e Sem ninguém (as que ninguém assumiu).',
              'Urgentes: paciente esperando há 1 h ou mais, ou janela de 24 h fechando em menos de 2 h.',
              'Selo no avatar: ponto rosa é "precisa de resposta", cadeado é "janela fechada", tique verde é "resolvida".',
              'Selo à direita: quem assumiu a conversa. Amarelo é você; colorido, outra pessoa.',
            ],
          },
          {
            titulo: 'Responder',
            itens: [
              'Resposta vai para o WhatsApp do paciente. Nota interna fica só na equipe, nunca chega ao paciente.',
              'O clipe anexa foto, áudio ou documento. A estrela insere uma mensagem pronta (você cria em Configurações).',
              'Janela de 24 h: depois que o paciente escreve, você responde à vontade por 24 h. Passou disso, só com template aprovado.',
              'Número em Coexistência: o médico também pode responder pelo WhatsApp Business do celular, sem janela e sem custo.',
            ],
          },
          {
            titulo: 'Organizar',
            itens: [
              'Assumir: a conversa fica com você e os outros veem que ela tem dono. Soltar devolve para a fila.',
              'Resolver: tira a conversa da fila quando não há mais nada a fazer. Se o paciente escrever de novo, ela volta.',
              'Tag: etiqueta para achar depois, como "aguardando exame".',
              'Ficha do contato (botão à direita do topo): as consultas com este médico, as com outros médicos à parte, e com quem mais o paciente conversa.',
              'Marcar: escolha em "Marcar com" o médico da consulta. Por padrão é o desta conversa.',
            ],
          },
        ],
        dica: 'Exemplo de rotina: filtre "Urgentes", assuma, responda e resolva. Repita até zerar.',
      },
      clientes: {
        titulo: 'Como funciona a lista de Clientes',
        resumo: 'Cada cliente é um médico que você atende.',
        secoes: [
          {
            titulo: 'As colunas',
            itens: [
              'Conexão: se o WhatsApp do médico está no ar ou com problema.',
              'Conversas abertas agora: quantas esperam alguma ação.',
              'Pacientes neste mês: quantos falaram com o médico no mês.',
              'Cobrança: se a sua mensalidade com ele está em dia, em aberto ou atrasada.',
            ],
          },
          {
            titulo: 'O que fazer aqui',
            itens: [
              'Novo cliente: cadastra o médico. Depois você conecta o WhatsApp dele na página do cliente.',
              'Clique num cliente para ver conexão, pacientes, templates, cobrança e atividade.',
            ],
          },
        ],
        dica: 'Ponto vermelho na coluna Conexão é prioridade: enquanto o WhatsApp está caído, nenhuma mensagem do médico chega.',
      },
      cliente_geral: {
        titulo: 'Visão geral do cliente',
        resumo: 'A saúde do WhatsApp deste médico e o volume de trabalho que ele gera.',
        secoes: [
          {
            titulo: 'Conexão',
            itens: [
              'Conectar WhatsApp: gera o link que o médico abre para conectar o número dele. Não precisa de cartão.',
              'Tipo de canal: Coexistência é o número que continua no WhatsApp Business do celular do médico.',
              'Qualidade: a nota que a Meta dá ao número. Baixa quer dizer muitos pacientes bloqueando ou denunciando.',
              'Tier de envio: para quantas pessoas diferentes o número pode mandar mensagem por dia.',
              'Sinal do app: em Coexistência, há quantos dias o médico não abre o WhatsApp Business. Sem uso por semanas, a Meta derruba a conexão.',
              'Conferir agora: lê de novo qualidade e limite na Meta.',
              'Desconectar número: tira o número da Zenda. As conversas ficam.',
            ],
          },
          {
            titulo: 'Volume',
            itens: [
              'Pacientes, conversas e consultas do mês, comparados com o mês passado. Serve para saber quanto trabalho o cliente dá.',
            ],
          },
        ],
        dica: 'Se aparecer um alerta em vermelho aqui, resolva antes de qualquer coisa: ele afeta todas as conversas do médico.',
      },
      cliente_pacientes: {
        titulo: 'Pacientes do cliente',
        resumo: 'Quem já falou com este médico pelo WhatsApp e quem você cadastrou à mão.',
        secoes: [
          {
            titulo: 'A lista',
            itens: [
              'Busca: por nome ou número.',
              'Mensagem: abre a conversa em Conversas, mesmo com quem nunca escreveu. Lá você registra a autorização do paciente, se faltar, e manda a primeira mensagem por template.',
              'A voltar: só quem tem consulta marcada daqui para frente. Bom para confirmar a semana.',
              'Novo paciente: cadastra pelo número do WhatsApp. Quando a pessoa escrever, a mensagem cai neste cadastro.',
            ],
          },
          {
            titulo: 'A ficha',
            itens: [
              'Identificação, contato, endereço e convênio. As alterações salvam sozinhas.',
              'Mensagens pelo WhatsApp: registre que o paciente autorizou receber mensagens. Sem isso, a primeira mensagem só vai para quem já escreveu.',
              'Dependentes: quem é atendido sem ser o dono do WhatsApp, como um filho. As mensagens vão para o responsável.',
              'Anotações: só a equipe vê. Ex.: "prefere manhã".',
            ],
          },
        ],
        dica: 'O número é o que identifica o paciente: cadastrar com o número certo evita ficha duplicada.',
      },
      cliente_templates: {
        titulo: 'Templates do cliente',
        resumo:
          'Mensagens prontas aprovadas pela Meta. São o único jeito de escrever para quem não fala há mais de 24 h.',
        secoes: [
          {
            titulo: 'Quando usar',
            itens: [
              'Lembrete de consulta, confirmação, retorno: tudo que você manda sem o paciente ter escrito antes.',
              'Cada template é da conta deste médico. Template de um médico não serve para outro.',
            ],
          },
          {
            titulo: 'Os estados',
            itens: [
              'Em análise: a Meta está revisando, normalmente em minutos.',
              'Aprovado: já aparece na conversa para enviar.',
              'Recusado: corrija o texto e crie outro. O recusado não volta.',
              'Pausado: muitos pacientes bloquearam. A Meta para o template por um tempo.',
            ],
          },
          {
            titulo: 'Categoria e custo',
            itens: [
              'Utilidade: aviso, lembrete, confirmação. Cerca de R$ 0,035 por mensagem.',
              'Marketing: promoção, novidade. Cerca de R$ 0,32, nove vezes mais. Se o texto parecer propaganda, a Meta muda para Marketing sozinha.',
            ],
          },
        ],
        dica: 'Exemplo que a Meta aprova: "Olá {{1}}, sua consulta é amanhã às {{2}}. Confirma?" Sem especialidade nem diagnóstico.',
      },
      cliente_cobranca: {
        titulo: 'Cobrança do cliente',
        resumo: 'O controle da mensalidade que você cobra deste médico.',
        secoes: [
          {
            titulo: 'Como funciona',
            itens: [
              'Cada mês aparece com vencimento e situação: em aberto, recebido ou em atraso.',
              'Marcar como recebido: quando o médico pagar. Desfazer corrige um engano.',
              'A Zenda não emite boleto nem cobra nada: é só um lembrete para você não perder a data.',
            ],
          },
        ],
        dica: 'Mensalidade atrasada aparece no Painel em "Cobranças em atraso".',
      },
      cliente_piloto: {
        titulo: 'Piloto automático',
        resumo: 'O que a IA sabe deste médico, quando ela responde e como testar antes de ligar.',
        secoes: [
          {
            titulo: 'Como funciona',
            itens: [
              'A IA responde como você, só com o que está na memória. O que não sabe, ela deixa para você em "Precisa de você".',
              'Pergunta de saúde, reclamação, teste de robô ou pedido de pessoa: ela para e a conversa fica "Assumida".',
              'Todo número, horário, valor e convênio da resposta é conferido na memória antes de sair.',
            ],
          },
          {
            titulo: 'Antes de ligar',
            itens: [
              'Escreva a memória e use o simulador, inclusive tentando enganar a IA.',
              'Monte o conjunto de teste e rode. Comece em copiloto.',
            ],
          },
        ],
        dica: 'Nenhuma mensagem do simulador vai para o WhatsApp.',
      },
      cliente_atividade: {
        titulo: 'Atividade do cliente',
        resumo: 'Quem fez o quê neste médico: mensagens, consultas, fichas.',
        secoes: [
          {
            titulo: 'Para que serve',
            itens: [
              'Ver o que a equipe fez, quando e por quem. Ex.: quem desmarcou uma consulta.',
              'Com equipe, mostra também quem de fora abriu cada conversa.',
            ],
          },
        ],
        dica: 'O conteúdo das mensagens não aparece aqui: só a ação e quem fez.',
      },
      agenda: {
        titulo: 'Como funciona a Agenda',
        resumo: 'A agenda de um médico por vez: dias, horários e consultas.',
        secoes: [
          {
            titulo: 'As abas',
            itens: [
              'Agenda: escolha Dia, Semana ou Mês à direita. As setas andam um dia, uma semana ou um mês, conforme a visão, e clicar no período abre o calendário para ir a qualquer data. Semana mostra a grade de horários, como no Google Agenda: o verde é onde o médico atende, e clicar num espaço vazio marca ali. Mês mostra o mês inteiro; clique num dia para abrir.',
              'Ao marcar pelo horário, a busca acha também pacientes de outros médicos. Escolher um deles o vincula a este médico, sem duplicar o cadastro.',
              'Disponibilidade: a rotina do médico (dias e horários em que atende) e as exceções, como férias ou um sábado extra.',
              'Integração: se a agenda do médico é o Doctoralia, o que é sincronizado e como.',
            ],
          },
          {
            titulo: 'Na consulta',
            itens: [
              'Remarcar, Paciente confirmou, Veio, Faltou e Desmarcar.',
              'Encaixe: consulta marcada por cima de outra, de propósito.',
              'Teleconsulta: consulta online.',
            ],
          },
          {
            titulo: 'Agenda no Doctoralia',
            itens: [
              'Quando o cliente usa Doctoralia, a verdade é lá: marcar aqui reserva lá primeiro, e o que o paciente marca lá aparece aqui sozinho.',
            ],
          },
        ],
        dica: 'Configure a disponibilidade antes de marcar: é ela que mostra os horários livres.',
      },
      equipe: {
        titulo: 'Como funciona a Equipe',
        resumo:
          'Para atender junto com outras atendentes. Cada uma decide quais médicos compartilha, e cada médico continua com quem o trouxe.',
        secoes: [
          {
            titulo: 'Papéis das pessoas',
            itens: [
              'Líder: quem criou a equipe. Muda papéis, passa a liderança, arquiva a equipe e vê o financeiro dos médicos.',
              'Admin: convida e remove membros. Ex.: a colega que ajuda a organizar a equipe.',
              'Membro: atende os médicos compartilhados. Ex.: quem cobre o plantão.',
            ],
          },
          {
            titulo: 'Níveis de cada médico compartilhado',
            itens: [
              'Atendimento: conversas, agenda e ficha do paciente. Para quem só atende.',
              'Gestão: tudo do atendimento, mais disponibilidade, locais e configuração da agenda. Para quem organiza a agenda do médico.',
              'Número, cartão e cobrança nunca são compartilhados: ficam com quem trouxe o médico.',
            ],
          },
          {
            titulo: 'Turnos',
            itens: [
              'Dizem quem cobre o quê e quando. Ex.: Ana de segunda a quarta, Bruna de quinta a sábado.',
              'Folga tira alguém de um dia; Extra coloca alguém num dia fora da rotina.',
              'Distribuir conversa nova: a conversa sem dono vai para quem está de plantão e com menos conversas abertas.',
              'Turno não tira acesso: fora do turno a pessoa continua vendo tudo, só deixa de receber conversa nova.',
            ],
          },
          {
            titulo: 'Convites e financeiro',
            itens: [
              'Convidar: pelo e-mail da pessoa no Zenda. Ela aceita o termo de confidencialidade para entrar.',
              'Financeiro: só a liderança vê, separado por operação, porque cada contrato é de quem trouxe o médico.',
            ],
          },
        ],
        dica: 'Exemplo: você tira férias. Compartilhe seus médicos com a Bruna no nível Atendimento, e ela cobre sem mexer na sua agenda.',
      },
      configuracoes: {
        titulo: 'Como funcionam as Configurações',
        resumo: 'Ajustes seus e textos que você reaproveita.',
        secoes: [
          {
            titulo: 'Seus ajustes',
            itens: [
              'Idioma: só muda a tela para você, não as mensagens aos pacientes.',
              'Seu nome na conversa: como a equipe vê você e o que entra no lugar de {atendente}.',
              'Avisos de mensagem nova: som e notificação neste navegador. "Fora do meu turno" limita os avisos aos médicos da sua operação.',
            ],
          },
          {
            titulo: 'Textos para reaproveitar',
            itens: [
              'Etiquetas: para organizar conversas. Valem para todos os clientes.',
              'Mensagens prontas: respostas que você manda sempre. Só saem com a janela de 24 h aberta. Variáveis como {paciente} se preenchem sozinhas.',
              'Modelos de template: texto base para criar o mesmo template em vários médicos. A aprovação é pedida na aba Templates de cada médico.',
            ],
          },
        ],
        dica: 'Diferença que confunde: mensagem pronta é para dentro das 24 h e não passa pela Meta; template é aprovado pela Meta e serve depois das 24 h.',
      },
    },
  },
};

export type Dictionary = typeof ptBR;
